import { HierarchyProperties } from "../components/canvasBuilder/properties/HierarchyProperties";
import TableChartIcon from "@mui/icons-material/TableChart";
import { RelatedRecordsProperties } from "../components/canvasBuilder/properties/RelatedRecordsProperties";
import {
  existingNestedChildDropRow,
  firstFreeNestedChildRow,
  insertCanvasBuilderElement,
  isDropBeforeSibling,
} from "./canvasBuilderDropOrder";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  CanvasTabHeaders,
  CanvasTabMoveContext,
} from "../components/canvasBuilder/CanvasTabHeaders";
import { useCanvasTabMove } from "../hooks/useCanvasTabMove";
import { adaptCanvasViewFields } from "./canvasViewFields";
import {
  Box,
  Typography,
  Sheet,
  Button,
  Input,
  Select,
  Option,
  Checkbox,
  Switch,
  FormControl,
  FormLabel,
  FormHelperText,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Card,
  CardContent,
  Chip,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  ButtonGroup,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionGroup,
  Textarea,
  ToggleButtonGroup,
} from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DatasetIcon from "@mui/icons-material/Dataset";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditNoteIcon from "@mui/icons-material/EditNote";
import GridOnIcon from "@mui/icons-material/GridOn";
import TimelineIcon from "@mui/icons-material/Timeline";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import ImageIcon from "@mui/icons-material/Image";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import TabIcon from "@mui/icons-material/Tab";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RuleIcon from "@mui/icons-material/Rule";
import SmartButtonIcon from "@mui/icons-material/SmartButton";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import VerifiedIcon from "@mui/icons-material/Verified";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletMacIcon from "@mui/icons-material/TabletMac";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import { useParams, useNavigate } from "react-router-dom";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragMoveEvent,
  DragOverlay,
  useDraggable,
} from "@dnd-kit/core";
import { useCanvasSensors, canvasDndAccessibility } from "../dnd/canvasSensors";
import { useMeasuredCanvasKeyboardStep } from "../dnd/useMeasuredCanvasKeyboardStep";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import {
  useCanvas,
  useCanvasMutations,
  useAvailableCanvasesForEmbedding,
} from "../hooks/useCanvases";
import { useViews } from "../hooks/useViews";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import { FloatingChatWidget } from "../components/MyVaultAgent";
import {
  CanvasGrid,
  CanvasGridElement,
  GridDropZone,
  VisibilityRulesEditor,
  CanvasLayoutFindings,
} from "../components/canvasBuilder";
import NestedCanvasGridElement from "../components/canvasBuilder/NestedCanvasGridElement";
import ActionButtonProperties from "../components/canvasBuilder/properties/ActionButtonProperties";
import ElementGrantsEditor from "../components/canvasBuilder/ElementGrantsEditor";
import CopyPublishedLink from "../components/canvasBuilder/CopyPublishedLink";
import type { RenderableElement } from "../components/canvasBuilder";
import type { BreakpointKind } from "../responsive/breakpoints";
import {
  projectRenderableElements,
  type CanvasBuilderElement,
} from "./canvasBuilderProjection";
import { applyCanvasBuilderElementDefaults } from "./canvasBuilderElementDefaults";
import {
  buildChildCanvasUpdatePayload,
  buildCanvasMutationPayload,
  generateCanvasDataSourceAlias,
  VIEW_GRID_REQUIRES_DATA_SOURCE,
} from "./canvasBuilderPayload";
import { hydrateCanvasBuilderContent } from "./canvasBuilderHydration";
import { CanvasTypeSelect } from "./CanvasTypeSelect";
import { canvasTypeFromDefinition } from "./canvasArchetype";
import { ViewGridFilterControlsEditor } from "./ViewGridFilterControlsEditor";
import {
  planInlineCanvasBackNavigation,
  planInlineCanvasEntry,
  type CanvasIdentifier,
} from "./canvasBuilderNavigation";
import {
  isContainerElement,
  wouldCreateCycle,
  getNextDisplayOrder,
} from "../hooks/useCanvasElements";
import type { ContainerElementConfig } from "../types";
import type {
  CanvasElement,
  CanvasElementType,
  CanvasDataSource,
  CanvasViewGridFilterControl,
  CanvasType,
  ViewField,
  CanvasGridConfig,
  AutoFillConfig,
  AutoFillType,
  AutoFillUserField,
  ValidationRule,
  ValidationRuleType,
} from "../types";
import { ELEMENT_MIN_SIZES } from "../types";
import {
  evaluateLayoutFindings,
  validateNoOverlapsByScope,
} from "../utils/canvasLayoutFindings";
import {
  getCanvasBuilderChildren,
  getCanvasBuilderContainerVariant,
  getCanvasBuilderInternalGridColumns,
  getNestedChildPositionError,
  projectCanvasLayoutInputs,
  removeCanvasBuilderElementSubtree,
} from "./canvasBuilderLayout";
import {
  findDeepestVisibleContainerAtPosition,
  findVisibleCanvasPanelId,
  resolveCanvasDropPanelId,
} from "./canvasBuilderDropTarget";
import { CanvasBuilderLivePreview } from "./CanvasBuilderLivePreview";

/** Runtime-equivalent grid config for preview mode */
const RUNTIME_GRID_CONFIG: CanvasGridConfig = {
  columns: 12,
  rowHeight: 60,
  gap: 8,
  minRows: 3,
};

/**
 * Representative viewport width (px) shown for each breakpoint in the builder
 * preview. Each value sits inside its breakpoint band (spec 7.2: desktop
 * >=1200, tablet 768-1199, phone <768) so the preview pane visibly matches the
 * device size. The breakpoint the renderer maps against is passed explicitly as
 * `forcedBreakpoint`, so the mapping is deterministic and does not depend on the
 * pane's actual measured width.
 */
const PREVIEW_WIDTHS: Record<BreakpointKind, number> = {
  desktop: 1280,
  tablet: 900,
  phone: 390,
};

type ElementTypeOption = {
  type: CanvasElementType;
  label: string;
  icon: React.ReactNode;
  category: "layout" | "content" | "data" | "actions" | "advanced";
  defaultColumnSpan: number;
  defaultRowSpan: number;
  /** Container type for container elements */
  containerType?: "SECTION" | "CARD" | "TABS" | "COLUMNS";
  /** Optional default props for preset element configurations */
  defaultProps?: Record<string, unknown>;
};

const ELEMENT_TYPES: ElementTypeOption[] = [
  // Layout containers
  {
    type: "container",
    label: "Section",
    icon: <ViewColumnIcon />,
    category: "layout",
    defaultColumnSpan: 12,
    defaultRowSpan: 4,
    containerType: "SECTION",
  },
  {
    type: "container",
    label: "Card",
    icon: <CreditCardIcon />,
    category: "layout",
    defaultColumnSpan: 6,
    defaultRowSpan: 4,
    containerType: "CARD",
  },
  {
    type: "container",
    label: "Tabs",
    icon: <TabIcon />,
    category: "layout",
    defaultColumnSpan: 12,
    defaultRowSpan: 6,
    containerType: "TABS",
  },
  {
    type: "container",
    label: "Columns",
    icon: <ViewWeekIcon />,
    category: "layout",
    defaultColumnSpan: 12,
    defaultRowSpan: 4,
    containerType: "COLUMNS",
  },

  // Content elements
  {
    type: "text",
    label: "Text",
    icon: <TextFieldsIcon />,
    category: "content",
    defaultColumnSpan: 4,
    defaultRowSpan: 1,
  },
  {
    type: "divider",
    label: "Divider",
    icon: <HorizontalRuleIcon />,
    category: "content",
    defaultColumnSpan: 12,
    defaultRowSpan: 1,
  },
  {
    type: "image",
    label: "Image",
    icon: <ImageIcon />,
    category: "content",
    defaultColumnSpan: 6,
    defaultRowSpan: 4,
  },

  // Data elements
  {
    type: "field",
    label: "Data Field",
    icon: <DatasetIcon />,
    category: "data",
    defaultColumnSpan: 4,
    defaultRowSpan: 1,
  },
  {
    type: "view_grid",
    label: "View Grid",
    icon: <GridOnIcon />,
    category: "data",
    defaultColumnSpan: 12,
    defaultRowSpan: 6,
    defaultProps: {
      title: "Browse records",
      visible_columns: [],
      page_size: 25,
      enable_search: true,
      enable_sort: true,
      enable_pagination: true,
      allow_create: false,
      allow_edit: false,
      allow_delete: false,
    },
  },
  {
    type: "widget",
    label: "Chart",
    icon: <DashboardIcon />,
    category: "data",
    defaultColumnSpan: 6,
    defaultRowSpan: 5,
    defaultProps: {
      widget_type: "CHART",
      title: "Chart",
      chart_config: {
        chart_type: "bar",
        x_field: "",
        y_fields: [""],
        aggregation: "sum",
      },
    },
  },
  {
    type: "widget",
    label: "Metric",
    icon: <DashboardIcon />,
    category: "data",
    defaultColumnSpan: 3,
    defaultRowSpan: 2,
    defaultProps: {
      widget_type: "METRIC",
      title: "Metric",
      metric_config: {
        value_field: "id",
        aggregation: "count",
        format: "number",
      },
    },
  },
  {
    type: "widget",
    label: "Summary Table",
    icon: <GridOnIcon />,
    category: "data",
    defaultColumnSpan: 8,
    defaultRowSpan: 5,
    defaultProps: {
      widget_type: "TABLE",
      title: "Summary Table",
      table_config: {
        columns: [
          { field: "", label: "Group" },
          { field: "id", label: "Count", aggregation: "count" },
        ],
      },
    },
  },

  {
    type: "timeline",
    label: "Timeline",
    icon: <TimelineIcon />,
    category: "data",
    defaultColumnSpan: 8,
    defaultRowSpan: 5,
    defaultProps: {
      widget_type: "TIMELINE",
      title: "Status history",
      label_field: "",
      status_field: "",
      date_field: "",
      description_field: "",
      order_field: "",
    },
  },

  {
    type: "kanban",
    label: "Kanban Board",
    icon: <ViewKanbanIcon />,
    category: "data",
    defaultColumnSpan: 12,
    defaultRowSpan: 6,
    defaultProps: {
      widget_type: "KANBAN",
      title: "Board",
      status_field: "",
      columns: [],
      title_field: "",
      subtitle_field: "",
    },
  },

  {
    type: "calendar",
    label: "Calendar",
    icon: <CalendarMonthIcon />,
    category: "data",
    defaultColumnSpan: 12,
    defaultRowSpan: 6,
    defaultProps: {
      widget_type: "CALENDAR",
      title: "Calendar",
      date_field: "",
      end_date_field: "",
      title_field: "",
      default_view: "month",
    },
  },

  {
    type: "hierarchy",
    label: "Hierarchy",
    icon: <TableChartIcon />,
    category: "data",
    defaultColumnSpan: 12,
    defaultRowSpan: 8,
    defaultProps: {
      widget_type: "HIERARCHY",
      title: "Hierarchy",
      hierarchy_config: {
        nodes: [],
        relationships: [],
        layout: "tree",
        initial_depth: 2,
        max_depth: 50,
      },
    },
  },

  {
    type: "related_records",
    label: "Related Records",
    icon: <TableChartIcon />,
    category: "data",
    defaultColumnSpan: 12,
    defaultRowSpan: 5,
    defaultProps: {
      title: "Related records",
      related_records_config: {
        related_data_source_alias: "",
        relationship_path: "",
        display_fields: [],
        max_records: 10,
        allow_create: false,
        allow_edit: false,
      },
    },
  },

  // Actions
  {
    type: "action_button",
    label: "Action Button",
    icon: <SmartButtonIcon />,
    category: "actions",
    defaultColumnSpan: 3,
    defaultRowSpan: 1,
    defaultProps: {
      label: "Run action",
      actionVariant: "solid",
      actionColor: "primary",
      confirm_before_run: true,
      confirmation: {
        enabled: true,
        title: "Run action",
        message: "Are you sure?",
      },
      direct_action: {
        type: "SEND_EMAIL",
        config: {},
      },
    },
  },

  // Advanced
  {
    type: "embedded_canvas",
    label: "Embedded Canvas",
    icon: <DashboardIcon />,
    category: "advanced",
    defaultColumnSpan: 12,
    defaultRowSpan: 6,
  },
];

// CanvasBuilderElement and the preview projection live in ./canvasBuilderProjection
// so the projection can be tested in isolation (audit finding 1).

// Draggable toolbox item component - ribbon style with icon above label
function ToolboxItem({
  typeOption,
  disabled = false,
}: {
  typeOption: ElementTypeOption;
  disabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `toolbox-${typeOption.label}`,
    data: { type: "toolbox-item", elementType: typeOption },
    disabled,
  });

  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      title={disabled ? VIEW_GRID_REQUIRES_DATA_SOURCE : undefined}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 0.75,
        minWidth: 48,
        borderRadius: "sm",
        opacity: isDragging || disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "grab",
        "&:hover": disabled ? undefined : { bgcolor: "neutral.softHoverBg" },
        "&:active": disabled
          ? undefined
          : { cursor: "grabbing", bgcolor: "neutral.softActiveBg" },
      }}
    >
      <Box sx={{ color: "neutral.600", mb: 0.25 }}>{typeOption.icon}</Box>
      <Typography
        level="body-xs"
        sx={{ fontSize: "10px", color: "text.secondary" }}
      >
        {typeOption.label}
      </Typography>
    </Box>
  );
}

/**
 * CanvasBuilder - Visual canvas app editor with grid-based layout
 */
export default function CanvasBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id && id !== "create";
  const canvasId = isEditing ? id : null;

  const { workspace, isAdmin } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  // Data fetching
  const {
    canvas,
    loading: canvasLoading,
    refresh: refreshCanvas,
  } = useCanvas(workspaceId, canvasId);
  const { views } = useViews(workspaceId);
  const {
    createCanvas,
    updateCanvas,
    loading: saving,
  } = useCanvasMutations(workspaceId);

  // Grid configuration - matches RUNTIME_GRID_CONFIG for WYSIWYG
  const [gridConfig] = useState<CanvasGridConfig>({
    columns: 12,
    rowHeight: 60,
    gap: 8,
    minRows: 6,
  });

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [canvasType, setCanvasType] = useState<CanvasType>("entry");
  // Explicit opt-in wizard authoring flag (spec is_wizard; audit R25). Persisted
  // as-is; default false. Set only by a deliberate author toggle below, never by
  // inference. Orthogonal to canvasType.
  const [isWizard, setIsWizard] = useState(false);
  const [elements, setElements] = useState<CanvasBuilderElement[]>([]);
  const [persistedElementIds, setPersistedElementIds] = useState<
    Record<string, string[]>
  >({});
  const [dataSources, setDataSources] = useState<CanvasDataSource[]>([]);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(
    null,
  );
  const [hoveredElementId, setHoveredElementId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const tabMove = useCanvasTabMove({
    workspaceId,
    canvasId,
    revision: canvas?.revision,
    dirty: isDirty,
    refresh: refreshCanvas,
    onError: setError,
    onDraftMove: (ownerId, panelId, index) => {
      setElements((current) =>
        current.map((element) => {
          if (element.tempId !== ownerId) return element;
          const panels = [
            ...((element.props.panels || []) as Array<{
              id: string;
              label: string;
            }>),
          ];
          const source = panels.findIndex((panel) => panel.id === panelId);
          if (source < 0 || index < 0 || index >= panels.length) return element;
          panels.splice(index, 0, panels.splice(source, 1)[0]);
          return { ...element, props: { ...element.props, panels } };
        }),
      );
      setIsDirty(true);
    },
  });
  const [activeTab, setActiveTab] = useState(0);
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const [showGrid, setShowGrid] = useState(true);
  // Which breakpoint the preview renders at (spec 7.2 requires a per-breakpoint
  // builder preview). Drives CanvasRenderer's forcedBreakpoint in preview mode.
  const [previewBreakpoint, setPreviewBreakpoint] =
    useState<BreakpointKind>("desktop");

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItemType, setDraggedItemType] =
    useState<ElementTypeOption | null>(null);
  const [draggedElementId, setDraggedElementId] = useState<string | null>(null);
  const [targetCell, setTargetCell] = useState<{
    row: number;
    col: number;
  } | null>(null);
  const [lastValidCell, setLastValidCell] = useState<{
    row: number;
    col: number;
  } | null>(null);
  const [hoveredContainerId, setHoveredContainerId] = useState<string | null>(
    null,
  );
  const { canvasAreaRef, canvasAreaNodeRef, keyboardStep } =
    useMeasuredCanvasKeyboardStep(gridConfig);

  // Breadcrumb navigation state for inline child canvas editing
  interface BreadcrumbItem {
    canvasId: CanvasIdentifier;
    canvasName: string;
    description: string;
    canvasType: CanvasType;
    isWizard: boolean;
    elementId?: string;
    elements: CanvasBuilderElement[];
    dataSources: CanvasDataSource[];
  }
  const [navBreadcrumbs, setNavBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [activeCanvasId, setActiveCanvasId] = useState<CanvasIdentifier | null>(
    canvasId,
  );
  const [childCanvasLoading, setChildCanvasLoading] = useState(false);

  useEffect(() => {
    if (navBreadcrumbs.length === 0) {
      setActiveCanvasId(canvasId);
    }
  }, [canvasId, navBreadcrumbs.length]);

  // dnd-kit sensors: pointer (unchanged 5px activation) plus a keyboard sensor
  // that moves the dragged element one grid cell per arrow key (spec section 9).
  // The measured cell pitch is passed so arrow keys step exactly one cell; a
  // null step (before first measurement) lets the sensor use its own fallback.
  const sensors = useCanvasSensors(keyboardStep ?? undefined);

  // Cache for full view data (with fields)
  const authFetch = useAuthFetch();
  const [viewFieldsCache, setViewFieldsCache] = useState<
    Record<string, ViewField[]>
  >({});
  const [loadingViewFields, setLoadingViewFields] = useState<
    Record<string, boolean>
  >({});

  // Generate unique ID for elements
  const generateId = () => crypto.randomUUID();

  // Calculate occupied cells
  const getOccupiedCells = useCallback(
    (excludeId?: string): Set<string> => {
      const occupied = new Set<string>();
      elements.forEach((el) => {
        if (el.tempId === excludeId) return;
        for (let r = el.gridRow; r < el.gridRow + el.rowSpan; r++) {
          for (let c = el.gridColumn; c < el.gridColumn + el.columnSpan; c++) {
            occupied.add(`${r},${c}`);
          }
        }
      });
      return occupied;
    },
    [elements],
  );

  // Find the deepest visible container under the pointer. DOM bounds are used
  // because nested containers have grid coordinates relative to their parent.
  const findContainerAtPosition = useCallback(
    (
      point: { x: number; y: number },
      excludeId?: string,
    ): CanvasBuilderElement | null => {
      const canvasArea = canvasAreaNodeRef.current;
      const elementNodes = canvasArea
        ? Array.from(
            canvasArea.querySelectorAll<HTMLElement>(
              "[data-canvas-element-id]",
            ),
          )
        : [];

      return findDeepestVisibleContainerAtPosition(
        elements,
        (element) =>
          element.tempId !== excludeId &&
          isContainerElement(element as unknown as CanvasElement),
        (element) => {
          const node = elementNodes.find(
            (candidate) => candidate.dataset.canvasElementId === element.tempId,
          );
          if (!node) return false;

          const rect = node.getBoundingClientRect();
          if (
            rect.width <= 0 ||
            rect.height <= 0 ||
            point.x < rect.left ||
            point.x > rect.right ||
            point.y < rect.top ||
            point.y > rect.bottom
          )
            return false;

          let ancestor: HTMLElement | null = node;
          while (ancestor) {
            if (
              ancestor.hasAttribute("hidden") ||
              ancestor.getAttribute("aria-hidden") === "true"
            )
              return false;
            const style = window.getComputedStyle(ancestor);
            if (style.display === "none" || style.visibility === "hidden")
              return false;
            if (ancestor === canvasArea) break;
            ancestor = ancestor.parentElement;
          }
          return true;
        },
      );
    },
    [canvasAreaNodeRef, elements],
  );

  // Check if a position is occupied
  const isCellOccupied = useCallback(
    (
      row: number,
      col: number,
      rowSpan = 1,
      colSpan = 1,
      excludeId?: string,
    ): boolean => {
      const occupied = getOccupiedCells(excludeId);
      for (let r = row; r < row + rowSpan; r++) {
        for (let c = col; c < col + colSpan; c++) {
          if (c > gridConfig.columns) return true; // Out of bounds
          if (occupied.has(`${r},${c}`)) return true;
        }
      }
      return false;
    },
    [getOccupiedCells, gridConfig.columns],
  );

  // Find the next available position
  const findNextAvailablePosition = useCallback(
    (columnSpan: number, rowSpan: number): { row: number; column: number } => {
      const occupied = getOccupiedCells();
      const maxRow = elements.reduce(
        (max, el) => Math.max(max, el.gridRow + el.rowSpan),
        1,
      );

      // Search for an empty spot
      for (let row = 1; row <= maxRow + 5; row++) {
        for (let col = 1; col <= gridConfig.columns - columnSpan + 1; col++) {
          let fits = true;
          for (let r = row; r < row + rowSpan && fits; r++) {
            for (let c = col; c < col + columnSpan && fits; c++) {
              if (occupied.has(`${r},${c}`)) fits = false;
            }
          }
          if (fits) return { row, column: col };
        }
      }

      // Default to first column of a new row
      return { row: maxRow + 1, column: 1 };
    },
    [elements, getOccupiedCells, gridConfig.columns],
  );

  // Calculate the maximum row used
  const maxRow = useMemo(() => {
    return elements.reduce(
      (max, el) => Math.max(max, el.gridRow + el.rowSpan - 1),
      0,
    );
  }, [elements]);

  // Live layout-validation findings for the author (spec 13.4; audit R25).
  // A frontend-checkable STRICT SUBSET of the backend engine: LAY001/002/003
  // block errors, LAY004 is a reported (never silent) min-size normalization.
  const layoutFindings = useMemo(
    () =>
      evaluateLayoutFindings(projectCanvasLayoutInputs(elements), {
        gridColumns: gridConfig.columns,
      }),
    [elements, gridConfig.columns],
  );

  // Calculate grid cell from mouse position (using top-left anchor with 10px offset)
  const getCellFromMousePosition = useCallback(
    (clientX: number, clientY: number): { row: number; col: number } | null => {
      if (!canvasAreaNodeRef.current) return null;

      const rect = canvasAreaNodeRef.current.getBoundingClientRect();
      const { rowHeight, gap, columns } = gridConfig;

      // Calculate position relative to canvas, offset by 10px to use "top-left corner"
      const relativeX = clientX - rect.left - 10;
      const relativeY = clientY - rect.top - 10;

      // Calculate cell dimensions
      const cellWidth = (rect.width - (columns - 1) * gap) / columns;

      // Calculate which column (1-based)
      const col = Math.floor(relativeX / (cellWidth + gap)) + 1;

      // Calculate which row (1-based)
      const row = Math.floor(relativeY / (rowHeight + gap)) + 1;

      return { row, col };
    },
    [canvasAreaNodeRef, gridConfig],
  );

  // Find the last valid cell position (for edge clamping)
  const findValidDropCell = useCallback(
    (
      rawRow: number,
      rawCol: number,
      rowSpan: number,
      colSpan: number,
      excludeId?: string,
    ): { row: number; col: number; valid: boolean } => {
      const { columns } = gridConfig;
      const totalRows = Math.max(maxRow + 5, gridConfig.minRows);

      // Clamp to grid bounds
      const clampedCol = Math.max(1, Math.min(rawCol, columns - colSpan + 1));
      const clampedRow = Math.max(1, Math.min(rawRow, totalRows - rowSpan + 1));

      // Check if the clamped position is valid (no overlap)
      const isValid = !isCellOccupied(
        clampedRow,
        clampedCol,
        rowSpan,
        colSpan,
        excludeId,
      );

      if (isValid) {
        return { row: clampedRow, col: clampedCol, valid: true };
      }

      // If clamped position is occupied, return it anyway but mark as invalid
      // The lastValidCell state will preserve the last good position
      return { row: clampedRow, col: clampedCol, valid: false };
    },
    [gridConfig, maxRow, isCellOccupied],
  );

  // Fetch full view data
  const fetchViewFields = useCallback(
    async (viewId: string) => {
      if (viewFieldsCache[viewId] || loadingViewFields[viewId]) return;

      setLoadingViewFields((prev) => ({ ...prev, [viewId]: true }));

      try {
        const response = await authFetch(
          `/api/workspace/myvault/views/${viewId}/full`,
          "fetch view for fields",
          { headers: { "x-workspace-id": workspaceId } },
        );
        setViewFieldsCache((prev) => ({
          ...prev,
          [viewId]: adaptCanvasViewFields(response.fields || []),
        }));
      } catch (err) {
        console.error("Failed to fetch view fields:", err);
        setViewFieldsCache((prev) => ({ ...prev, [viewId]: [] }));
      } finally {
        setLoadingViewFields((prev) => ({ ...prev, [viewId]: false }));
      }
    },
    [authFetch, workspaceId, viewFieldsCache, loadingViewFields],
  );

  // Fetch view fields when data sources have views assigned
  useEffect(() => {
    dataSources.forEach((ds) => {
      if (ds.view_id && !viewFieldsCache[String(ds.view_id)]) {
        fetchViewFields(String(ds.view_id));
      }
    });
  }, [dataSources, viewFieldsCache, fetchViewFields]);

  // Load existing canvas data
  useEffect(() => {
    if (isEditing && canvas) {
      setName(canvas.name);
      setDescription(canvas.description || "");
      setCanvasType(canvasTypeFromDefinition(canvas));
      // Round-trip the persisted flag as-is; absent -> false (never inferred).
      setIsWizard(canvas.is_wizard === true);

      const {
        elements: transformedElements,
        dataSources: transformedDataSources,
      } = hydrateCanvasBuilderContent(canvas, generateId);

      setElements(transformedElements);
      if (canvasId)
        setPersistedElementIds((current) => ({
          ...current,
          [canvasId]: transformedElements.map((element) => element.tempId),
        }));
      setDataSources(transformedDataSources);
    }
  }, [isEditing, canvas]);

  const selectedElement = useMemo(
    () => elements.find((el) => el.tempId === selectedElementId),
    [elements, selectedElementId],
  );

  // Transform builder elements to RenderableElement format for preview mode.
  // The projection carries nesting metadata so nested children stay nested in
  // preview (see canvasBuilderProjection).
  const renderableElements = useMemo(
    (): RenderableElement[] => projectRenderableElements(elements),
    [elements],
  );

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeId = String(active.id);
    setIsDragging(true);
    setTargetCell(null);
    setLastValidCell(null);

    if (activeId.startsWith("toolbox-")) {
      const data = active.data.current as { elementType: ElementTypeOption };
      setDraggedItemType(data.elementType);
      setDraggedElementId(null);
    } else {
      setDraggedItemType(null);
      setDraggedElementId(activeId);
    }
  };

  const dragPoint = useRef<{ x: number; y: number } | null>(null);
  const handleDragMove = (event: DragMoveEvent) => {
    const { active, activatorEvent } = event;
    const activeId = String(active.id);

    // Derive the drag anchor point in viewport coordinates. A pointer drag uses
    // the activator pointer position plus the live delta (unchanged behaviour).
    // A keyboard drag has no pointer coordinates on the activator event (it is a
    // KeyboardEvent), so the old cast to PointerEvent produced undefined
    // clientX/clientY and therefore NaN cells that never landed. For a keyboard
    // drag read the dragged element's current translated top-left corner from
    // dnd-kit instead. The +10 cancels the 10px "top-left corner" offset that
    // getCellFromMousePosition applies, so the element's true top-left maps to
    // the target cell.
    const pointer = activatorEvent as PointerEvent | null;
    const hasPointerCoords =
      pointer != null && typeof pointer.clientX === "number";

    let mouseX: number;
    let mouseY: number;
    if (hasPointerCoords) {
      mouseX = pointer.clientX + (event.delta?.x || 0);
      mouseY = pointer.clientY + (event.delta?.y || 0);
    } else {
      const translated = active.rect.current.translated;
      if (!translated) return;
      mouseX = translated.left + 10;
      mouseY = translated.top + 10;
    }

    dragPoint.current = { x: mouseX, y: mouseY };
    const rawCell = getCellFromMousePosition(mouseX, mouseY);
    if (!rawCell) return;

    // Determine element dimensions
    let rowSpan = 1;
    let colSpan = 1;
    let excludeId: string | undefined;

    if (activeId.startsWith("toolbox-")) {
      const data = active.data.current as { elementType: ElementTypeOption };
      rowSpan = data.elementType.defaultRowSpan;
      colSpan = data.elementType.defaultColumnSpan;
    } else {
      const element = elements.find((el) => el.tempId === activeId);
      if (element) {
        rowSpan = element.rowSpan;
        colSpan = element.columnSpan;
        excludeId = activeId;
      }
    }

    // Find valid drop position with clamping
    const result = findValidDropCell(
      rawCell.row,
      rawCell.col,
      rowSpan,
      colSpan,
      excludeId,
    );

    setTargetCell({ row: result.row, col: result.col });

    // Only update lastValidCell if position is valid
    if (result.valid) {
      setLastValidCell({ row: result.row, col: result.col });
    }

    // Check if hovering over a container (for nesting)
    const container = findContainerAtPosition(
      { x: mouseX, y: mouseY },
      excludeId,
    );
    setHoveredContainerId(container?.tempId || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active } = event;
    const activeId = String(active.id);

    // Use lastValidCell if available (clamped to last valid position)
    const dropCell = lastValidCell || targetCell;

    // Capture container ID before resetting state
    const targetContainerId = hoveredContainerId;
    const point = dragPoint.current;
    let targetPanelId: string | undefined;
    if (targetContainerId) {
      const panelNodes = Array.from(
        document.querySelectorAll<HTMLElement>(
          "[data-canvas-parent-id][data-canvas-panel-id]",
        ),
      ).filter((node) => node.dataset.canvasParentId === targetContainerId);
      const panelAtDrop =
        (point &&
          panelNodes.find((node) => {
            const rect = node.getBoundingClientRect();
            return (
              rect.width > 0 &&
              rect.height > 0 &&
              point.x >= rect.left &&
              point.x <= rect.right &&
              point.y >= rect.top &&
              point.y <= rect.bottom
            );
          })?.dataset.canvasPanelId) ||
        undefined;
      const activePanel = findVisibleCanvasPanelId(
        targetContainerId,
        panelNodes,
      );
      const targetContainer = elements.find(
        (element) => element.tempId === targetContainerId,
      );
      const containerType = targetContainer
        ? getCanvasBuilderContainerVariant(targetContainer)
        : undefined;
      const containerProps = targetContainer?.props as
        | {
            panels?: Array<{ id: string }>;
            defaultPanelId?: string;
          }
        | undefined;
      const configuredPanels = containerProps?.panels;
      const firstPanel =
        containerType === "tabs"
          ? containerProps?.defaultPanelId ||
            configuredPanels?.[0]?.id ||
            "tab-1"
          : containerType === "columns"
            ? "col-0"
            : undefined;
      targetPanelId = resolveCanvasDropPanelId(
        panelAtDrop,
        activePanel,
        firstPanel,
      );
    }
    const siblings = elements
      .filter(
        (el) =>
          el.tempId !== activeId &&
          el.parentElementId === (targetContainerId || undefined) &&
          el.parentPanelId === targetPanelId,
      )
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    let dropIndex = siblings.findIndex((sibling) => {
      if (!targetContainerId)
        return (
          !!dropCell &&
          (sibling.gridRow > dropCell.row ||
            (sibling.gridRow === dropCell.row &&
              sibling.gridColumn > dropCell.col))
        );
      const node = Array.from(
        document.querySelectorAll<HTMLElement>("[data-canvas-element-id]"),
      ).find((node) => node.dataset.canvasElementId === sibling.tempId);
      if (!node || !point) return false;
      const rect = node.getBoundingClientRect();
      return isDropBeforeSibling(point, rect);
    });
    if (dropIndex < 0) dropIndex = siblings.length;

    // Reset drag state
    setIsDragging(false);
    setDraggedItemType(null);
    setDraggedElementId(null);
    setTargetCell(null);
    setLastValidCell(null);
    setHoveredContainerId(null);

    // No valid drop position found
    if (!dropCell) return;

    // Handle drop from toolbox
    if (activeId.startsWith("toolbox-")) {
      const typeOption = (
        active.data.current as { elementType: ElementTypeOption }
      ).elementType;

      // Dropping into a container
      if (targetContainerId) {
        // Don't allow dropping containers into containers (for simplicity)
        if (typeOption.category === "layout") {
          return;
        }
        const parent = elements.find(
          (element) => element.tempId === targetContainerId,
        );
        if (!parent) return;
        const childRow = firstFreeNestedChildRow(
          elements,
          targetContainerId,
          targetPanelId,
          typeOption.defaultRowSpan,
          parent.rowSpan,
        );
        if (childRow === null) {
          setError(
            "This child does not fit. Enlarge the container before adding it.",
          );
          return;
        }
        // Add element as child of container
        handleAddElementAtPosition(
          typeOption,
          childRow,
          1,
          targetContainerId,
          targetPanelId,
          dropIndex,
        );
      } else {
        // Dropping on canvas root level
        if (
          !isCellOccupied(
            dropCell.row,
            dropCell.col,
            typeOption.defaultRowSpan,
            typeOption.defaultColumnSpan,
          )
        ) {
          handleAddElementAtPosition(
            typeOption,
            dropCell.row,
            dropCell.col,
            undefined,
            undefined,
            dropIndex,
          );
        }
      }
    } else {
      // Handle repositioning existing element
      const element = elements.find((el) => el.tempId === activeId);
      if (!element) return;

      // Dropping into a container
      if (targetContainerId) {
        // Don't allow dropping containers into containers (for simplicity)
        if (isContainerElement(element as unknown as CanvasElement)) {
          return;
        }
        // Don't allow dropping into self
        if (element.tempId === targetContainerId) {
          return;
        }
        const childRow = existingNestedChildDropRow(
          elements,
          activeId,
          targetContainerId,
          targetPanelId,
        );
        if (childRow === null) {
          setError(
            "This child does not fit. Enlarge the container before moving it.",
          );
          return;
        }
        handleDropElement(
          activeId,
          childRow,
          1,
          targetContainerId,
          targetPanelId,
          dropIndex,
        );
      } else if (element.parentElementId) {
        // Moving OUT of a container to root level
        if (
          !isCellOccupied(
            dropCell.row,
            dropCell.col,
            element.rowSpan,
            element.columnSpan,
            activeId,
          )
        ) {
          handleDropElement(
            activeId,
            dropCell.row,
            dropCell.col,
            undefined,
            undefined,
            dropIndex,
          );
        }
      } else {
        // Moving on root level
        if (
          !isCellOccupied(
            dropCell.row,
            dropCell.col,
            element.rowSpan,
            element.columnSpan,
            activeId,
          )
        ) {
          handleDropElement(
            activeId,
            dropCell.row,
            dropCell.col,
            undefined,
            undefined,
            dropIndex,
          );
        }
      }
    }
  };

  const handleAddElementAtPosition = useCallback(
    (
      typeOption: ElementTypeOption,
      row: number,
      column: number,
      parentId?: string,
      panelId?: string,
      index?: number,
    ) => {
      if (
        (typeOption.type === "view_grid" ||
          typeOption.type === "widget" ||
          typeOption.type === "timeline" ||
          typeOption.type === "kanban" ||
          typeOption.type === "calendar" ||
          typeOption.type === "related_records" ||
          typeOption.type === "hierarchy") &&
        !dataSources.some((source) => source.view_id)
      ) {
        setError(
          typeOption.type === "timeline"
            ? "Add a data source before adding a Timeline"
            : typeOption.type === "kanban"
              ? "Add a data source before adding a Kanban board"
              : typeOption.type === "hierarchy"
                ? "Add item Views before adding a Hierarchy"
                : typeOption.type === "related_records"
                  ? "Add source and related Views before adding Related Records"
                  : typeOption.type === "calendar"
                    ? "Add a data source before adding a Calendar"
                    : VIEW_GRID_REQUIRES_DATA_SOURCE,
        );
        return;
      }

      // Build base props
      const baseProps: CanvasElement["props"] = {
        label: typeOption.label,
        placeholder: "",
        required: false,
        disabled: false,
        variant: typeOption.label.toLowerCase().replace(" ", "-"),
        // Apply required defaults, including the paragraph discriminator for Text.
        ...applyCanvasBuilderElementDefaults(
          typeOption.type,
          typeOption.defaultProps,
        ),
      };

      if (
        typeOption.type === "view_grid" ||
        typeOption.type === "widget" ||
        typeOption.type === "timeline" ||
        typeOption.type === "kanban" ||
        typeOption.type === "calendar" ||
        typeOption.type === "related_records" ||
        typeOption.type === "hierarchy"
      ) {
        const source = dataSources.find((candidate) => candidate.view_id);
        if (source) {
          baseProps.data_source_alias = source.id;
          baseProps.visible_columns = [];
        }
      }

      // Add container-specific config for container types
      if (typeOption.category === "layout" && typeOption.containerType) {
        const containerType = typeOption.containerType;
        const containerConfig: ContainerElementConfig = {
          containerType,
          title: typeOption.label,
        };

        // Add default panels for Tabs
        if (containerType === "TABS") {
          containerConfig.panels = [
            { id: crypto.randomUUID(), label: "Tab 1" },
            { id: crypto.randomUUID(), label: "Tab 2" },
          ];
          containerConfig.defaultPanelId = containerConfig.panels[0].id;
        }

        // Add default columnWidths for Columns
        if (containerType === "COLUMNS") {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (containerConfig as any).columnWidths = ["1fr", "1fr"];
        }

        Object.assign(baseProps, containerConfig);
      }

      const newElement: CanvasBuilderElement = {
        tempId: generateId(),
        id: generateId(),
        type: typeOption.type,
        props: baseProps,
        layout: {
          order: elements.length,
        },
        gridColumn: column,
        gridRow: row,
        columnSpan: typeOption.defaultColumnSpan,
        rowSpan: typeOption.defaultRowSpan,
        parentElementId: parentId,
        parentPanelId: panelId,
        displayOrder: getNextDisplayOrder(
          elements as unknown as CanvasElement[],
          parentId,
          panelId,
        ),
      };
      setElements((prev) =>
        insertCanvasBuilderElement([...prev, newElement], newElement.tempId, {
          parentId,
          panelId,
          index: index ?? prev.length,
          row,
          column,
        }),
      );
      setSelectedElementId(newElement.tempId);
      setIsDirty(true);
    },
    [dataSources, elements],
  );

  const handleDropElement = (
    tempId: string,
    row: number,
    column: number,
    parentId: string | undefined,
    panelId: string | undefined,
    index: number,
  ) => {
    if (
      parentId &&
      wouldCreateCycle(elements as unknown as CanvasElement[], tempId, parentId)
    )
      return;
    const persisted = persistedElementIds[String(canvasId)] || [];
    const item = elements.find((el) => el.tempId === tempId);
    if (!item) return;
    const parent = parentId
      ? elements.find((el) => el.tempId === parentId)
      : undefined;
    const savedParent = canvas?.elements?.find((el) => el.id === parent?.id);
    const savedConfig = ((
      savedParent as unknown as { config_json?: Record<string, unknown> }
    )?.config_json ||
      savedParent?.props ||
      {}) as {
      panels?: Array<{ id: string }>;
      column_widths?: unknown[];
      columnWidths?: unknown[];
    };
    const savedPanels = savedConfig.panels || [];
    const savedColumns =
      savedConfig.column_widths || savedConfig.columnWidths || [];
    const existingTarget =
      !parentId ||
      (persisted.includes(parentId) &&
        (!panelId ||
          savedPanels.some((panel) => panel.id === panelId) ||
          savedColumns.some((_, index) => panelId === `col-${index}`)));
    if (canvasId && persisted.includes(tempId) && existingTarget) {
      void tabMove.moveElement({
        itemId: item.id || item.tempId,
        parentId: parent?.id,
        panelId,
        index,
        row,
        column,
      });
      return;
    }
    setElements((current) =>
      insertCanvasBuilderElement(current, tempId, {
        parentId,
        panelId,
        index,
        row,
        column,
      }),
    );
    setIsDirty(true);
  };

  const handleResizeElement = useCallback(
    (tempId: string, columnSpan: number, rowSpan: number) => {
      setElements((prev) =>
        prev.map((el) => {
          if (el.tempId !== tempId) return el;

          // Enforce minimum sizes based on element type
          const minSizes = ELEMENT_MIN_SIZES[el.type] || {
            minRowSpan: 1,
            minColumnSpan: 1,
          };
          const finalRowSpan = Math.max(rowSpan, minSizes.minRowSpan);
          const finalColumnSpan = Math.max(columnSpan, minSizes.minColumnSpan);

          return { ...el, columnSpan: finalColumnSpan, rowSpan: finalRowSpan };
        }),
      );
      setIsDirty(true);
    },
    [],
  );

  const handleUpdateNestedPosition = useCallback(
    (tempId: string, field: "gridRow" | "gridColumn", value: number) => {
      const element = elements.find((candidate) => candidate.tempId === tempId);
      if (!element) return;
      const row = field === "gridRow" ? value : element.gridRow;
      const column = field === "gridColumn" ? value : element.gridColumn;
      const positionError = getNestedChildPositionError(
        elements,
        tempId,
        row,
        column,
      );
      if (positionError) {
        setError(positionError);
        return;
      }
      setError(null);
      setElements((previous) =>
        previous.map((candidate) =>
          candidate.tempId === tempId
            ? { ...candidate, [field]: value }
            : candidate,
        ),
      );
      setIsDirty(true);
    },
    [elements],
  );

  const handleRemoveElement = useCallback(
    (tempId: string) => {
      setElements((prev) => removeCanvasBuilderElementSubtree(prev, tempId));
      if (selectedElementId === tempId) {
        setSelectedElementId(null);
      }
      setIsDirty(true);
    },
    [selectedElementId],
  );

  const handleDuplicateElement = useCallback(
    (tempId: string) => {
      const element = elements.find((el) => el.tempId === tempId);
      if (!element) return;

      const pos = findNextAvailablePosition(
        element.columnSpan,
        element.rowSpan,
      );
      const newElement: CanvasBuilderElement = {
        ...element,
        tempId: generateId(),
        id: generateId(),
        gridRow: pos.row,
        gridColumn: pos.column,
        layout: { ...element.layout, order: elements.length },
      };
      setElements((prev) => [...prev, newElement]);
      setSelectedElementId(newElement.tempId);
      setIsDirty(true);
    },
    [elements, findNextAvailablePosition],
  );

  const handleUpdateElementProps = useCallback(
    (tempId: string, props: Partial<CanvasElement["props"]>) => {
      setElements((prev) =>
        prev.map((el) =>
          el.tempId === tempId
            ? { ...el, props: { ...el.props, ...props } }
            : el,
        ),
      );
      setIsDirty(true);
    },
    [],
  );

  const handleUpdateElementBinding = useCallback(
    (tempId: string, binding: CanvasElement["data_binding"] | undefined) => {
      setElements((prev) =>
        prev.map((el) =>
          el.tempId === tempId ? { ...el, data_binding: binding } : el,
        ),
      );
      setIsDirty(true);
    },
    [],
  );

  const handleAddDataSource = useCallback(() => {
    const newDs: CanvasDataSource = {
      id: `ds-${Date.now()}`,
      name: `Data Source ${dataSources.length + 1}`,
      type: "view",
      view_id: undefined,
      config: {},
    };
    setDataSources((prev) => [...prev, newDs]);
    setIsDirty(true);
  }, [dataSources.length]);

  const handleUpdateDataSource = useCallback(
    (dsId: string, updates: Partial<CanvasDataSource>) => {
      setDataSources((prev) =>
        prev.map((ds) => (ds.id === dsId ? { ...ds, ...updates } : ds)),
      );
      setIsDirty(true);
    },
    [],
  );

  const handleRemoveDataSource = useCallback((dsId: string) => {
    setDataSources((prev) => prev.filter((ds) => ds.id !== dsId));
    setIsDirty(true);
  }, []);

  // Navigate into child canvas for inline editing
  const handleNavigateToChildCanvas = useCallback(
    async (childCanvasId: string | number, elementId?: string) => {
      if (!canvasId) return;

      try {
        setChildCanvasLoading(true);

        // Save current canvas state to breadcrumbs
        const currentSnapshot: Omit<BreadcrumbItem, "canvasId"> = {
          canvasName: name,
          description,
          canvasType,
          isWizard,
          elementId,
          elements: [...elements],
          dataSources: [...dataSources],
        };

        // Fetch child canvas data
        const childCanvas = await authFetch(
          `/api/workspace/myvault/canvases/${childCanvasId}/full`,
          "fetch child canvas for editing",
          { headers: { "x-workspace-id": workspaceId } },
        );

        const childContent = hydrateCanvasBuilderContent(
          childCanvas,
          generateId,
        );
        const navigation = planInlineCanvasEntry({
          activeCanvasId: activeCanvasId ?? canvasId,
          childCanvasId,
          breadcrumbs: navBreadcrumbs,
          currentSnapshot,
        });

        // Update state
        setNavBreadcrumbs(navigation.breadcrumbs);
        setActiveCanvasId(navigation.activeCanvasId);
        setName(childCanvas.name || "Child Canvas");
        setDescription(childCanvas.description || "");
        setCanvasType(canvasTypeFromDefinition(childCanvas));
        setIsWizard(childCanvas.is_wizard === true);
        setElements(childContent.elements);
        setPersistedElementIds((current) => ({
          ...current,
          [String(childCanvasId)]: childContent.elements.map(
            (element) => element.tempId,
          ),
        }));
        setDataSources(childContent.dataSources);
        setSelectedElementId(null);
      } catch (err) {
        console.error("Failed to load child canvas:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load child canvas",
        );
      } finally {
        setChildCanvasLoading(false);
      }
    },
    [
      canvasId,
      activeCanvasId,
      navBreadcrumbs,
      name,
      description,
      canvasType,
      isWizard,
      elements,
      dataSources,
      authFetch,
      workspaceId,
    ],
  );

  const renderNestedCanvasElement = (
    element: CanvasBuilderElement,
  ): React.ReactNode => {
    const parent = elements.find(
      (candidate) => candidate.tempId === element.parentElementId,
    );
    const nestedGridConfig = parent
      ? {
          ...gridConfig,
          columns: getCanvasBuilderInternalGridColumns(parent),
        }
      : gridConfig;

    return (
      <NestedCanvasGridElement
        key={element.tempId}
        element={element}
        config={nestedGridConfig}
        mode={mode}
        isSelected={selectedElementId === element.tempId}
        isHovered={hoveredElementId === element.tempId}
        isDragging={draggedElementId === element.tempId}
        isDropTarget={
          isDragging &&
          hoveredContainerId === element.tempId &&
          isContainerElement(element as unknown as CanvasElement)
        }
        onSelect={(additive) => {
          if (additive) {
            // Multi-select (future feature)
          }
          setSelectedElementId(element.tempId);
        }}
        onHover={(isHovered) =>
          setHoveredElementId(isHovered ? element.tempId : null)
        }
        onDelete={() => handleRemoveElement(element.tempId)}
        onDuplicate={() => handleDuplicateElement(element.tempId)}
        onResize={(columnSpan, rowSpan) =>
          handleResizeElement(element.tempId, columnSpan, rowSpan)
        }
      >
        {renderElementPreview(
          element,
          elements,
          selectedElementId,
          hoveredElementId,
          handleNavigateToChildCanvas,
          renderNestedCanvasElement,
        )}
      </NestedCanvasGridElement>
    );
  };

  // Navigate back to parent canvas from breadcrumbs
  const handleNavigateBack = useCallback(
    async (targetIndex: number) => {
      if (targetIndex < 0 || targetIndex >= navBreadcrumbs.length) return;

      try {
        setChildCanvasLoading(true);

        if (activeCanvasId === null || canvasId === null) return;

        const navigation = planInlineCanvasBackNavigation({
          routeCanvasId: canvasId,
          activeCanvasId,
          breadcrumbs: navBreadcrumbs,
          targetIndex,
        });

        // Save current child canvas changes first
        if (navBreadcrumbs.length > 0) {
          if (isDirty) {
            // Auto-save child canvas
            const savePayload = buildChildCanvasUpdatePayload({
              name,
              description,
              canvasType,
              isWizard,
              elements,
              dataSources,
            });

            await authFetch(
              `/api/workspace/myvault/canvases/${navigation.saveCanvasId}`,
              "save child canvas",
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  "x-workspace-id": workspaceId,
                },
                body: JSON.stringify(savePayload),
              },
            );
          }
        }

        // Get target breadcrumb to restore
        const targetBreadcrumb = navigation.targetBreadcrumb;

        // Restore state from breadcrumb
        setActiveCanvasId(targetBreadcrumb.canvasId);
        setName(targetBreadcrumb.canvasName);
        setDescription(targetBreadcrumb.description);
        setCanvasType(targetBreadcrumb.canvasType);
        setIsWizard(targetBreadcrumb.isWizard);
        setElements(targetBreadcrumb.elements);
        setDataSources(targetBreadcrumb.dataSources);
        setSelectedElementId(null);
        setNavBreadcrumbs(navigation.remainingBreadcrumbs);
        setIsDirty(false);
      } catch (err) {
        console.error("Failed to navigate back:", err);
        setError(
          err instanceof Error ? err.message : "Failed to navigate back",
        );
      } finally {
        setChildCanvasLoading(false);
      }
    },
    [
      navBreadcrumbs,
      canvasId,
      activeCanvasId,
      name,
      description,
      canvasType,
      isWizard,
      elements,
      dataSources,
      isDirty,
      authFetch,
      workspaceId,
    ],
  );

  // Check if we're editing a child canvas
  const isEditingChildCanvas = navBreadcrumbs.length > 0;

  const getOrCreateDataSourceForView = useCallback(
    (viewId: string): string => {
      const existing = dataSources.find((ds) => ds.view_id === viewId);
      if (existing) return existing.id;

      const view = views.find((v) => String(v.id) === viewId);
      const newDs: CanvasDataSource = {
        id: `ds-${Date.now()}`,
        name: view?.name || `Data Source ${dataSources.length + 1}`,
        type: "view",
        view_id: viewId,
        config: {},
      };
      setDataSources((prev) => [...prev, newDs]);
      setIsDirty(true);
      fetchViewFields(viewId);
      return newDs.id;
    },
    [dataSources, views, fetchViewFields],
  );

  const getElementViewId = useCallback(
    (element: CanvasBuilderElement): string => {
      if (!element.data_binding?.data_source_id) return "";
      const ds = dataSources.find(
        (d) => d.id === element.data_binding?.data_source_id,
      );
      return ds?.view_id ? String(ds.view_id) : "";
    },
    [dataSources],
  );

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Canvas name is required");
      return;
    }

    const invalidDataSources = dataSources.filter((ds) => !ds.view_id);
    if (invalidDataSources.length > 0) {
      setError("All data sources must have a view selected");
      return;
    }

    // Validate no overlapping elements
    const overlapErrors = validateNoOverlapsByScope(
      projectCanvasLayoutInputs(elements),
    );

    if (overlapErrors.length > 0) {
      setError(`Canvas has overlapping elements: ${overlapErrors[0].message}`);
      return;
    }

    setError(null);

    try {
      const payload = buildCanvasMutationPayload({
        name,
        description,
        canvasType,
        isWizard,
        elements,
        dataSources,
      });
      if (isEditing && canvasId) {
        await updateCanvas(canvasId, {
          name: payload.name,
          description: payload.description,
          archetype: payload.archetype,
          is_wizard: payload.is_wizard,
          elements: payload.elements,
          data_sources: payload.data_sources,
        });
      } else {
        await createCanvas(payload);
      }
      navigate(`/workspace/${workspaceId}/myvault/canvases`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save canvas");
    }
  };

  const handleCancel = () => {
    navigate(`/workspace/${workspaceId}/myvault/canvases`);
  };

  const getElementTypeLabel = (element: CanvasBuilderElement): string => {
    const variant = element.props.variant as string;
    const typeOption = ELEMENT_TYPES.find(
      (t) => t.label.toLowerCase().replace(" ", "-") === variant,
    );
    return typeOption?.label || element.type;
  };

  const loading = isEditing && canvasLoading && !canvas;

  if (!workspaceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">Please select a workspace first.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Generate drop zones for dragging
  const renderDropZones = () => {
    if (!isDragging) return null;

    const zones = [];
    const totalRows = Math.max(maxRow + 5, gridConfig.minRows);
    const occupied = getOccupiedCells(draggedElementId || undefined);

    // Determine element dimensions for multi-cell highlight
    let rowSpan = 1;
    let colSpan = 1;
    if (draggedItemType) {
      rowSpan = draggedItemType.defaultRowSpan;
      colSpan = draggedItemType.defaultColumnSpan;
    } else if (draggedElementId) {
      const element = elements.find((el) => el.tempId === draggedElementId);
      if (element) {
        rowSpan = element.rowSpan;
        colSpan = element.columnSpan;
      }
    }

    // Use lastValidCell for visual indicator (clamped position)
    const displayCell = lastValidCell || targetCell;

    for (let row = 1; row <= totalRows; row++) {
      for (let col = 1; col <= gridConfig.columns; col++) {
        const isOccupied = occupied.has(`${row},${col}`);

        // Check if this cell is part of the target drop area
        let isTargetArea = false;
        if (displayCell) {
          const targetRowStart = displayCell.row;
          const targetColStart = displayCell.col;
          const targetRowEnd = displayCell.row + rowSpan - 1;
          const targetColEnd = displayCell.col + colSpan - 1;
          isTargetArea =
            row >= targetRowStart &&
            row <= targetRowEnd &&
            col >= targetColStart &&
            col <= targetColEnd;
        }

        // Check if this target position would overlap
        const isInvalidTarget = isTargetArea && isOccupied;

        zones.push(
          <GridDropZone
            key={`${row}-${col}`}
            row={row}
            column={col}
            isOccupied={isOccupied}
            isTarget={isTargetArea}
            isInvalidTarget={isInvalidTarget}
            config={gridConfig}
          />,
        );
      }
    }
    return zones;
  };

  return (
    <DndContext
      sensors={sensors}
      accessibility={canvasDndAccessibility}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
    >
      <Box
        aria-busy={tabMove.moving}
        ref={(node: HTMLDivElement | null) =>
          node?.toggleAttribute("inert", tabMove.moving)
        }
        sx={{ height: "100vh", display: "flex", flexDirection: "column" }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <IconButton variant="plain" onClick={handleCancel}>
            <ArrowBackIcon />
          </IconButton>
          <Input
            variant="plain"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setIsDirty(true);
            }}
            placeholder="Canvas name"
            sx={{ fontWeight: "lg", fontSize: "lg", flex: 1, maxWidth: 400 }}
          />
          <CanvasTypeSelect
            value={canvasType}
            onChange={(value) => {
              setCanvasType(value);
              setIsDirty(true);
            }}
          />
          {/* Explicit opt-in wizard authoring flag (spec is_wizard; audit R25).
              A deliberate author choice, persisted as-is; never inferred. */}
          <Tooltip title="Author this canvas as a step-by-step wizard. Explicit opt-in; saved with the canvas.">
            <Checkbox
              size="sm"
              label="Wizard"
              checked={isWizard}
              onChange={(event) => {
                setIsWizard(event.target.checked);
                setIsDirty(true);
              }}
              slotProps={{ input: { "aria-label": "Author as wizard" } }}
            />
          </Tooltip>
          <Box sx={{ flex: 1 }} />

          {canvasId && canvas && (
            <CopyPublishedLink
              canvasId={canvasId}
              isPublished={
                canvas.status === "published" ||
                (canvas as typeof canvas & { is_published?: boolean })
                  .is_published === true
              }
              isAdmin={isAdmin}
            />
          )}

          {/* Grid toggle */}
          {mode === "edit" && (
            <Tooltip title={showGrid ? "Hide grid" : "Show grid"}>
              <IconButton
                variant={showGrid ? "soft" : "plain"}
                color={showGrid ? "primary" : "neutral"}
                onClick={() => setShowGrid(!showGrid)}
              >
                <GridOnIcon />
              </IconButton>
            </Tooltip>
          )}

          <ButtonGroup variant="outlined" color="neutral" size="sm">
            <Button
              startDecorator={<EditNoteIcon />}
              variant={mode === "edit" ? "solid" : "outlined"}
              color={mode === "edit" ? "primary" : "neutral"}
              onClick={() => setMode("edit")}
            >
              Edit
            </Button>
            <Button
              startDecorator={<VisibilityIcon />}
              variant={mode === "preview" ? "solid" : "outlined"}
              color={mode === "preview" ? "primary" : "neutral"}
              onClick={() => setMode("preview")}
            >
              Preview
            </Button>
          </ButtonGroup>

          <Button variant="plain" color="neutral" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            startDecorator={<SaveIcon />}
            onClick={handleSave}
            loading={saving}
            disabled={!isDirty && isEditing}
          >
            Save
          </Button>
        </Box>

        {error && (
          <Alert color="danger" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Live layout-validation findings surface (spec 13.4; audit R25).
            Renders nothing when the canvas is clean; otherwise shows blocking
            errors and reported (never silent) min-size normalizations. */}
        {layoutFindings.length > 0 && (
          <Box sx={{ mx: 2, mt: 2 }}>
            <CanvasLayoutFindings
              findings={layoutFindings}
              data-testid="canvas-layout-findings"
            />
          </Box>
        )}

        {/* Breadcrumb Navigation - shown when editing child canvases */}
        {isEditingChildCanvas && (
          <Sheet
            variant="soft"
            sx={{
              px: 2,
              py: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Typography level="body-xs" sx={{ color: "text.tertiary", mr: 1 }}>
              Editing:
            </Typography>
            {/* Root canvas link */}
            <Chip
              variant="soft"
              color="primary"
              size="sm"
              startDecorator={<HomeIcon sx={{ fontSize: 14 }} />}
              onClick={() => handleNavigateBack(0)}
              sx={{ cursor: "pointer" }}
            >
              {navBreadcrumbs[0]?.canvasName || "Parent Canvas"}
            </Chip>
            {/* Intermediate breadcrumbs */}
            {navBreadcrumbs.slice(1).map((crumb, index) => (
              <React.Fragment key={crumb.canvasId}>
                <ChevronRightIcon
                  sx={{ fontSize: 16, color: "text.tertiary" }}
                />
                <Chip
                  variant="soft"
                  color="neutral"
                  size="sm"
                  onClick={() => handleNavigateBack(index + 1)}
                  sx={{ cursor: "pointer" }}
                >
                  {crumb.canvasName}
                </Chip>
              </React.Fragment>
            ))}
            {/* Current canvas indicator */}
            <ChevronRightIcon sx={{ fontSize: 16, color: "text.tertiary" }} />
            <Chip
              variant="solid"
              color="primary"
              size="sm"
              startDecorator={<DashboardIcon sx={{ fontSize: 14 }} />}
            >
              {name}
            </Chip>
            {childCanvasLoading && (
              <CircularProgress size="sm" sx={{ ml: 1 }} />
            )}
          </Sheet>
        )}

        {/* Main Content */}
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Center - Canvas with Grid */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              bgcolor: "background.level1",
            }}
          >
            {/* Horizontal Element Palette - ribbon style */}
            {mode === "edit" && (
              <Sheet
                variant="soft"
                sx={{
                  px: 2,
                  py: 0.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "stretch",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                {/* Layout group */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    level="body-xs"
                    sx={{
                      fontSize: "9px",
                      color: "text.tertiary",
                      mb: 0.25,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Layout
                  </Typography>
                  <Stack direction="row" spacing={0}>
                    {ELEMENT_TYPES.filter((t) => t.category === "layout").map(
                      (typeOption) => (
                        <ToolboxItem
                          key={typeOption.label}
                          typeOption={typeOption}
                        />
                      ),
                    )}
                  </Stack>
                </Box>
                <Divider orientation="vertical" />
                {/* Content group */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    level="body-xs"
                    sx={{
                      fontSize: "9px",
                      color: "text.tertiary",
                      mb: 0.25,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Content
                  </Typography>
                  <Stack direction="row" spacing={0}>
                    {ELEMENT_TYPES.filter((t) => t.category === "content").map(
                      (typeOption) => (
                        <ToolboxItem
                          key={typeOption.label}
                          typeOption={typeOption}
                        />
                      ),
                    )}
                  </Stack>
                </Box>
                <Divider orientation="vertical" />
                {/* Data group */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    level="body-xs"
                    sx={{
                      fontSize: "9px",
                      color: "text.tertiary",
                      mb: 0.25,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Data
                  </Typography>
                  <Stack direction="row" spacing={0}>
                    {ELEMENT_TYPES.filter((t) => t.category === "data").map(
                      (typeOption) => (
                        <ToolboxItem
                          key={typeOption.label}
                          typeOption={typeOption}
                          disabled={
                            (typeOption.type === "view_grid" ||
                              typeOption.type === "widget" ||
                              typeOption.type === "timeline" ||
                              typeOption.type === "kanban" ||
                              typeOption.type === "calendar" ||
                              typeOption.type === "related_records" ||
                              typeOption.type === "hierarchy") &&
                            !dataSources.some((source) => source.view_id)
                          }
                        />
                      ),
                    )}
                  </Stack>
                </Box>
                <Divider orientation="vertical" />
                {/* Actions group */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    level="body-xs"
                    sx={{
                      fontSize: "9px",
                      color: "text.tertiary",
                      mb: 0.25,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Actions
                  </Typography>
                  <Stack direction="row" spacing={0}>
                    {ELEMENT_TYPES.filter((t) => t.category === "actions").map(
                      (typeOption) => (
                        <ToolboxItem
                          key={typeOption.label}
                          typeOption={typeOption}
                        />
                      ),
                    )}
                  </Stack>
                </Box>
                <Divider orientation="vertical" />
                {/* Advanced group */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    level="body-xs"
                    sx={{
                      fontSize: "9px",
                      color: "text.tertiary",
                      mb: 0.25,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Advanced
                  </Typography>
                  <Stack direction="row" spacing={0}>
                    {ELEMENT_TYPES.filter((t) => t.category === "advanced").map(
                      (typeOption) => (
                        <ToolboxItem
                          key={typeOption.label}
                          typeOption={typeOption}
                        />
                      ),
                    )}
                  </Stack>
                </Box>
              </Sheet>
            )}

            {/* Canvas area */}
            <Box
              sx={{
                flex: 1,
                p: 3,
                overflow: "auto",
              }}
            >
              {/* Preview mode - use CanvasRenderer for true WYSIWYG */}
              {mode === "preview" ? (
                <Box sx={{ maxWidth: 1280, mx: "auto" }}>
                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Chip
                      color="primary"
                      variant="soft"
                      startDecorator={<VisibilityIcon />}
                    >
                      Preview Mode - Exact Runtime View
                    </Chip>
                    {/* Per-breakpoint preview control (spec 7.2). Selecting a
                        breakpoint drives the renderer's forcedBreakpoint and
                        constrains the preview pane to that device width. */}
                    <ToggleButtonGroup
                      value={previewBreakpoint}
                      onChange={(_, value) => {
                        if (value)
                          setPreviewBreakpoint(value as BreakpointKind);
                      }}
                      size="sm"
                      aria-label="Preview breakpoint"
                    >
                      <Button
                        value="desktop"
                        startDecorator={<DesktopWindowsIcon />}
                        aria-label="Desktop preview"
                      >
                        Desktop
                      </Button>
                      <Button
                        value="tablet"
                        startDecorator={<TabletMacIcon />}
                        aria-label="Tablet preview"
                      >
                        Tablet
                      </Button>
                      <Button
                        value="phone"
                        startDecorator={<SmartphoneIcon />}
                        aria-label="Phone preview"
                      >
                        Phone
                      </Button>
                    </ToggleButtonGroup>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                      {previewBreakpoint} - {PREVIEW_WIDTHS[previewBreakpoint]}
                      px
                    </Typography>
                  </Box>
                  <Box
                    data-testid="canvas-preview-pane"
                    sx={{
                      width: PREVIEW_WIDTHS[previewBreakpoint],
                      maxWidth: "100%",
                      mx: "auto",
                      transition: "width 0.2s ease",
                    }}
                  >
                    <Card>
                      <CardContent>
                        <CanvasBuilderLivePreview
                          elements={renderableElements}
                          config={RUNTIME_GRID_CONFIG}
                          workspaceId={workspaceId}
                          canvasId={
                            activeCanvasId === null
                              ? canvasId
                              : String(activeCanvasId)
                          }
                          dataSources={dataSources}
                          title={name || "Untitled Canvas"}
                          description={description}
                          breakpoint={previewBreakpoint}
                          themeConfig={
                            (canvas as unknown as { theme_config?: unknown })
                              ?.theme_config
                          }
                          color={
                            (canvas as unknown as { color?: string })?.color
                          }
                        />
                      </CardContent>
                    </Card>
                  </Box>
                </Box>
              ) : (
                /* Edit mode - interactive canvas */
                <Card sx={{ maxWidth: 900, mx: "auto", overflow: "visible" }}>
                  <CardContent>
                    <Typography level="h4" sx={{ mb: 1 }}>
                      {name || "Untitled Canvas"}
                    </Typography>
                    {description && (
                      <Typography
                        level="body-sm"
                        sx={{ mb: 3, color: "text.secondary" }}
                      >
                        {description}
                      </Typography>
                    )}

                    {/* Grid-based canvas area */}
                    <Box
                      id="canvas-area"
                      ref={canvasAreaRef}
                      sx={{
                        position: "relative",
                        display: "grid",
                        gridTemplateColumns: `repeat(${gridConfig.columns}, 1fr)`,
                        gridAutoRows: gridConfig.rowHeight,
                        gap: `${gridConfig.gap}px`,
                        minHeight:
                          Math.max(maxRow + 3, gridConfig.minRows) *
                          (gridConfig.rowHeight + gridConfig.gap),
                        p: 1,
                        borderRadius: "md",
                        border: "2px dashed",
                        borderColor: isDragging
                          ? "primary.300"
                          : "neutral.outlinedBorder",
                        bgcolor: "background.level2",
                      }}
                      onClick={() => setSelectedElementId(null)}
                    >
                      {/* Visual grid overlay */}
                      <CanvasGrid
                        config={gridConfig}
                        maxRow={maxRow}
                        visible={showGrid}
                      />

                      {/* Drop zones (only during drag) */}
                      {renderDropZones()}

                      {/* Canvas elements */}
                      {elements.length === 0 && !isDragging ? (
                        <Box
                          sx={{
                            gridColumn: "1 / -1",
                            gridRow: "1 / span 3",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            level="body-sm"
                            sx={{ color: "text.tertiary" }}
                          >
                            Drag elements from the left panel onto the canvas
                          </Typography>
                        </Box>
                      ) : (
                        // Only render root-level elements (those without a parent)
                        elements
                          .filter((el) => !el.parentElementId)
                          .map((element) => (
                            <CanvasGridElement
                              key={element.tempId}
                              id={element.tempId}
                              gridColumn={element.gridColumn}
                              gridRow={element.gridRow}
                              columnSpan={element.columnSpan}
                              rowSpan={element.rowSpan}
                              isSelected={selectedElementId === element.tempId}
                              isHovered={hoveredElementId === element.tempId}
                              isDragging={draggedElementId === element.tempId}
                              isDropTarget={
                                isDragging &&
                                hoveredContainerId === element.tempId &&
                                isContainerElement(
                                  element as unknown as CanvasElement,
                                )
                              }
                              mode={mode}
                              config={gridConfig}
                              label={element.props.label as string}
                              elementType={getElementTypeLabel(element)}
                              isBound={!!element.data_binding}
                              onSelect={(additive) => {
                                if (additive) {
                                  // Multi-select (future feature)
                                }
                                setSelectedElementId(element.tempId);
                              }}
                              onHover={(isHovered) =>
                                setHoveredElementId(
                                  isHovered ? element.tempId : null,
                                )
                              }
                              onDelete={() =>
                                handleRemoveElement(element.tempId)
                              }
                              onDuplicate={() =>
                                handleDuplicateElement(element.tempId)
                              }
                              onResize={(colSpan, rowSpan) =>
                                handleResizeElement(
                                  element.tempId,
                                  colSpan,
                                  rowSpan,
                                )
                              }
                            >
                              <CanvasTabMoveContext.Provider
                                value={{
                                  move: tabMove.move,
                                  disabled:
                                    saving ||
                                    tabMove.moving ||
                                    (isEditing && isDirty),
                                  reason:
                                    isEditing && isDirty
                                      ? "Save changes before reordering tabs."
                                      : undefined,
                                }}
                              >
                                {renderElementPreview(
                                  element,
                                  elements,
                                  selectedElementId,
                                  hoveredElementId,
                                  handleNavigateToChildCanvas,
                                  renderNestedCanvasElement,
                                )}
                              </CanvasTabMoveContext.Provider>
                            </CanvasGridElement>
                          ))
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>

          {/* Right Panel - Properties & Data Sources */}
          {mode === "edit" && (
            <Sheet
              variant="soft"
              sx={{
                width: 300,
                borderLeft: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value as number)}
                sx={{ flex: 1, display: "flex", flexDirection: "column" }}
              >
                <TabList sx={{ px: 2, pt: 1 }}>
                  <Tab>Properties</Tab>
                  <Tab>Advanced</Tab>
                </TabList>

                <TabPanel value={0} sx={{ flex: 1, overflow: "auto", p: 2 }}>
                  {selectedElement ? (
                    <Stack spacing={2}>
                      <Typography level="title-sm">
                        {getElementTypeLabel(selectedElement)}
                      </Typography>

                      {/* Position info */}
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Chip size="sm" variant="outlined">
                          Col {selectedElement.gridColumn}
                        </Chip>
                        <Chip size="sm" variant="outlined">
                          Row {selectedElement.gridRow}
                        </Chip>
                        <Chip size="sm" variant="outlined">
                          {selectedElement.columnSpan}×{selectedElement.rowSpan}
                        </Chip>
                      </Box>

                      {selectedElement.parentElementId && (
                        <Box>
                          <Typography level="title-sm" sx={{ mb: 1 }}>
                            Position in parent grid
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <FormControl>
                              <FormLabel>Row</FormLabel>
                              <Input
                                type="number"
                                slotProps={{ input: { min: 1, step: 1 } }}
                                value={selectedElement.gridRow}
                                onChange={(event) =>
                                  handleUpdateNestedPosition(
                                    selectedElement.tempId,
                                    "gridRow",
                                    Number(event.target.value),
                                  )
                                }
                                aria-label="Child row"
                              />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Column</FormLabel>
                              <Input
                                type="number"
                                slotProps={{ input: { min: 1, step: 1 } }}
                                value={selectedElement.gridColumn}
                                onChange={(event) =>
                                  handleUpdateNestedPosition(
                                    selectedElement.tempId,
                                    "gridColumn",
                                    Number(event.target.value),
                                  )
                                }
                                aria-label="Child column"
                              />
                            </FormControl>
                          </Stack>
                        </Box>
                      )}

                      {isAdmin &&
                        activeCanvasId &&
                        persistedElementIds[String(activeCanvasId)]?.includes(
                          selectedElement.tempId,
                        ) && (
                          <ElementGrantsEditor
                            workspaceId={workspaceId}
                            canvasId={String(activeCanvasId)}
                            elementId={selectedElement.tempId}
                          />
                        )}

                      <FormControl>
                        <FormLabel>Label</FormLabel>
                        <Input
                          value={selectedElement.props.label || ""}
                          onChange={(e) =>
                            handleUpdateElementProps(selectedElement.tempId, {
                              label: e.target.value,
                            })
                          }
                        />
                      </FormControl>

                      {selectedElement.type === "field" && (
                        <>
                          <FormControl>
                            <FormLabel>Placeholder</FormLabel>
                            <Input
                              value={selectedElement.props.placeholder || ""}
                              onChange={(e) =>
                                handleUpdateElementProps(
                                  selectedElement.tempId,
                                  {
                                    placeholder: e.target.value,
                                  },
                                )
                              }
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Helper Text</FormLabel>
                            <Input
                              value={selectedElement.props.helperText || ""}
                              onChange={(e) =>
                                handleUpdateElementProps(
                                  selectedElement.tempId,
                                  {
                                    helperText: e.target.value,
                                  },
                                )
                              }
                            />
                          </FormControl>

                          <Stack direction="row" spacing={2}>
                            <Checkbox
                              label="Required"
                              checked={!!selectedElement.props.required}
                              onChange={(e) =>
                                handleUpdateElementProps(
                                  selectedElement.tempId,
                                  {
                                    required: e.target.checked,
                                  },
                                )
                              }
                            />
                            <Checkbox
                              label="Disabled"
                              checked={!!selectedElement.props.disabled}
                              onChange={(e) =>
                                handleUpdateElementProps(
                                  selectedElement.tempId,
                                  {
                                    disabled: e.target.checked,
                                  },
                                )
                              }
                            />
                          </Stack>

                          <Divider />

                          <Typography level="title-sm">Data Binding</Typography>

                          {views.length === 0 ? (
                            <Typography
                              level="body-sm"
                              sx={{ color: "text.tertiary" }}
                            >
                              Create a View first to bind this field to data
                            </Typography>
                          ) : (
                            <>
                              <FormControl>
                                <FormLabel>View</FormLabel>
                                <Select
                                  value={getElementViewId(selectedElement)}
                                  onChange={(_, value) => {
                                    if (value) {
                                      const dsId = getOrCreateDataSourceForView(
                                        value as string,
                                      );
                                      handleUpdateElementBinding(
                                        selectedElement.tempId,
                                        {
                                          data_source_id: dsId,
                                          field_path: "",
                                        },
                                      );
                                    } else {
                                      handleUpdateElementBinding(
                                        selectedElement.tempId,
                                        undefined,
                                      );
                                    }
                                  }}
                                  placeholder="Select view"
                                >
                                  <Option value="">None</Option>
                                  {views.map((v) => (
                                    <Option key={v.id} value={String(v.id)}>
                                      {v.name}
                                    </Option>
                                  ))}
                                </Select>
                              </FormControl>

                              {selectedElement.data_binding?.data_source_id &&
                                (() => {
                                  const viewId =
                                    getElementViewId(selectedElement);
                                  const viewFields = viewId
                                    ? viewFieldsCache[viewId] || []
                                    : [];
                                  const isLoadingFields = viewId
                                    ? loadingViewFields[viewId]
                                    : false;

                                  return (
                                    <FormControl>
                                      <FormLabel>Field</FormLabel>
                                      {isLoadingFields ? (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <CircularProgress size="sm" />
                                          <Typography
                                            level="body-sm"
                                            sx={{ color: "text.tertiary" }}
                                          >
                                            Loading fields...
                                          </Typography>
                                        </Box>
                                      ) : viewFields.length > 0 ? (
                                        <Select
                                          value={
                                            selectedElement.data_binding
                                              ?.field_path || ""
                                          }
                                          onChange={(_, value) => {
                                            const selectedField =
                                              viewFields.find(
                                                (f) => String(f.id) === value,
                                              );
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            const sf = selectedField as any;
                                            const displayName =
                                              selectedField?.display_name ||
                                              sf?.display_label ||
                                              selectedField?.column
                                                ?.display_name ||
                                              selectedField?.alias ||
                                              sf?.source_table_alias ||
                                              selectedField?.column
                                                ?.column_name ||
                                              sf?.column_id;

                                            handleUpdateElementBinding(
                                              selectedElement.tempId,
                                              {
                                                data_source_id:
                                                  selectedElement.data_binding!
                                                    .data_source_id,
                                                field_path: value as string,
                                              },
                                            );

                                            // Auto-set label from field name if not already set
                                            if (
                                              displayName &&
                                              !selectedElement.props.label
                                            ) {
                                              handleUpdateElementProps(
                                                selectedElement.tempId,
                                                {
                                                  label: displayName,
                                                },
                                              );
                                            }
                                          }}
                                          placeholder="Select field"
                                        >
                                          <Option value="">None</Option>
                                          {viewFields.map((field) => {
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            const f = field as any;
                                            const fieldName =
                                              field.display_name ||
                                              f.display_label ||
                                              field.column?.display_name ||
                                              field.alias ||
                                              f.source_table_alias ||
                                              field.column?.column_name ||
                                              f.column_id ||
                                              "Unnamed Field";
                                            return (
                                              <Option
                                                key={field.id}
                                                value={field.id}
                                              >
                                                {fieldName}
                                              </Option>
                                            );
                                          })}
                                        </Select>
                                      ) : (
                                        <Typography
                                          level="body-sm"
                                          sx={{ color: "text.tertiary" }}
                                        >
                                          No fields found in this view
                                        </Typography>
                                      )}
                                    </FormControl>
                                  );
                                })()}
                            </>
                          )}
                        </>
                      )}

                      {selectedElement.type === "text" && (
                        <>
                          <FormControl>
                            <FormLabel>Content</FormLabel>
                            <Textarea
                              minRows={2}
                              maxRows={6}
                              value={selectedElement.props.label || ""}
                              onChange={(e) =>
                                handleUpdateElementProps(
                                  selectedElement.tempId,
                                  {
                                    label: e.target.value,
                                  },
                                )
                              }
                              placeholder="Enter text content..."
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Level</FormLabel>
                            <Select
                              value={String(
                                selectedElement.props.level || "paragraph",
                              )}
                              onChange={(_, value) => {
                                const level =
                                  value === "paragraph"
                                    ? undefined
                                    : (parseInt(value as string, 10) as
                                        1 | 2 | 3 | 4 | 5 | 6);
                                handleUpdateElementProps(
                                  selectedElement.tempId,
                                  {
                                    level,
                                    textVariant: level ? "heading" : "text",
                                  },
                                );
                              }}
                            >
                              <Option value="paragraph">Paragraph</Option>
                              <Option value="1">H1 - Heading 1</Option>
                              <Option value="2">H2 - Heading 2</Option>
                              <Option value="3">H3 - Heading 3</Option>
                              <Option value="4">H4 - Heading 4</Option>
                              <Option value="5">H5 - Heading 5</Option>
                              <Option value="6">H6 - Heading 6</Option>
                            </Select>
                            <FormHelperText>
                              Choose paragraph for body text or a heading level
                            </FormHelperText>
                          </FormControl>

                          <FormControl>
                            <Checkbox
                              label="Render as HTML"
                              checked={!!selectedElement.props.renderAsHtml}
                              onChange={(e) =>
                                handleUpdateElementProps(
                                  selectedElement.tempId,
                                  {
                                    renderAsHtml: e.target.checked,
                                    textVariant: e.target.checked
                                      ? "html"
                                      : selectedElement.props.level
                                        ? "heading"
                                        : "text",
                                  },
                                )
                              }
                            />
                            <FormHelperText>
                              Enable to render HTML markup in the content
                            </FormHelperText>
                          </FormControl>

                          <FormControl>
                            <FormLabel>Alignment</FormLabel>
                            <ToggleButtonGroup
                              value={selectedElement.props.textAlign || "left"}
                              onChange={(_, value) =>
                                handleUpdateElementProps(
                                  selectedElement.tempId,
                                  {
                                    textAlign: value || "left",
                                  },
                                )
                              }
                            >
                              <Button value="left">
                                <FormatAlignLeftIcon />
                              </Button>
                              <Button value="center">
                                <FormatAlignCenterIcon />
                              </Button>
                              <Button value="right">
                                <FormatAlignRightIcon />
                              </Button>
                            </ToggleButtonGroup>
                          </FormControl>
                        </>
                      )}

                      {/* Columns container configuration */}
                      {selectedElement.type === "container" &&
                        ["SECTION", "CARD"].includes(
                          String(
                            selectedElement.props.containerType || "SECTION",
                          ),
                        ) &&
                        (() => {
                          const binding = selectedElement.props
                            .selection_binding as
                            | import("../types").CanvasSelectionBinding
                            | undefined;
                          const primaryIndex = dataSources.findIndex(
                            (source, index) => source.is_primary ?? index === 0,
                          );
                          const primary = dataSources[primaryIndex];
                          const primaryAlias = primary
                            ? generateCanvasDataSourceAlias(
                                primary.name,
                                primaryIndex,
                              )
                            : "";
                          const grids = elements.filter(
                            (element) =>
                              element.type === "view_grid" &&
                              [
                                primary?.id,
                                primary?.name,
                                primaryAlias,
                              ].includes(
                                String(element.props.data_source_alias || ""),
                              ),
                          );
                          return (
                            <>
                              <Divider />
                              <Typography level="title-sm">
                                Selected-record detail
                              </Typography>
                              <FormControl>
                                <FormLabel>Source grid</FormLabel>
                                <Select
                                  aria-label="Detail source grid"
                                  value={binding?.source_grid_element_id || ""}
                                  onChange={(_, value) =>
                                    handleUpdateElementProps(
                                      selectedElement.tempId,
                                      {
                                        selection_binding: value
                                          ? {
                                              source_grid_element_id: value,
                                              data_source_alias: primaryAlias,
                                              mode: binding?.mode || "read",
                                              empty_state_text:
                                                binding?.empty_state_text ||
                                                "Select a record to view details.",
                                            }
                                          : undefined,
                                      },
                                    )
                                  }
                                >
                                  <Option value="">None</Option>
                                  {grids.map((grid) => (
                                    <Option
                                      key={grid.id || grid.tempId}
                                      value={grid.id || grid.tempId}
                                    >
                                      {String(
                                        grid.props.title ||
                                          grid.props.label ||
                                          "View grid",
                                      )}
                                    </Option>
                                  ))}
                                </Select>
                                <FormHelperText>
                                  Only grids on the primary data source can
                                  drive details.
                                </FormHelperText>
                              </FormControl>
                              {binding && (
                                <>
                                  <FormControl>
                                    <FormLabel>Detail mode</FormLabel>
                                    <Select
                                      aria-label="Detail mode"
                                      value={binding.mode}
                                      onChange={(_, value) =>
                                        value &&
                                        handleUpdateElementProps(
                                          selectedElement.tempId,
                                          {
                                            selection_binding: {
                                              ...binding,
                                              mode: value,
                                            },
                                          },
                                        )
                                      }
                                    >
                                      <Option value="read">Read only</Option>
                                      <Option value="edit">Editable</Option>
                                    </Select>
                                  </FormControl>
                                  <FormControl>
                                    <FormLabel>Empty-state text</FormLabel>
                                    <Input
                                      aria-label="Detail empty-state text"
                                      value={binding.empty_state_text}
                                      onChange={(event) =>
                                        handleUpdateElementProps(
                                          selectedElement.tempId,
                                          {
                                            selection_binding: {
                                              ...binding,
                                              empty_state_text:
                                                event.target.value,
                                            },
                                          },
                                        )
                                      }
                                    />
                                  </FormControl>
                                </>
                              )}
                            </>
                          );
                        })()}
                      {selectedElement.type === "container" &&
                        (
                          selectedElement.props as unknown as {
                            containerType?: string;
                          }
                        ).containerType === "COLUMNS" && (
                          <>
                            <Divider />
                            <Typography level="title-sm">
                              Column Layout
                            </Typography>
                            <FormControl>
                              <FormLabel>Number of Columns</FormLabel>
                              <Select
                                value={String(
                                  (
                                    selectedElement.props as unknown as {
                                      columnWidths?: string[];
                                    }
                                  ).columnWidths?.length || 2,
                                )}
                                onChange={(_, value) => {
                                  const numCols = parseInt(value as string, 10);
                                  const newWidths = Array(numCols).fill("1fr");
                                  handleUpdateElementProps(
                                    selectedElement.tempId,
                                    {
                                      columnWidths: newWidths,
                                    } as unknown as Partial<
                                      CanvasElement["props"]
                                    >,
                                  );
                                }}
                              >
                                <Option value="2">2 Columns</Option>
                                <Option value="3">3 Columns</Option>
                                <Option value="4">4 Columns</Option>
                              </Select>
                            </FormControl>

                            {(
                              (
                                selectedElement.props as unknown as {
                                  columnWidths?: string[];
                                }
                              ).columnWidths || ["1fr", "1fr"]
                            ).map((width, index) => (
                              <FormControl key={index}>
                                <FormLabel>Column {index + 1} Width</FormLabel>
                                <Select
                                  value={width}
                                  onChange={(_, value) => {
                                    const currentWidths = (
                                      selectedElement.props as unknown as {
                                        columnWidths?: string[];
                                      }
                                    ).columnWidths || ["1fr", "1fr"];
                                    const newWidths = [...currentWidths];
                                    newWidths[index] = value as string;
                                    handleUpdateElementProps(
                                      selectedElement.tempId,
                                      {
                                        columnWidths: newWidths,
                                      } as unknown as Partial<
                                        CanvasElement["props"]
                                      >,
                                    );
                                  }}
                                >
                                  <Option value="1fr">1x (Equal)</Option>
                                  <Option value="2fr">2x (Double)</Option>
                                  <Option value="3fr">3x (Triple)</Option>
                                </Select>
                              </FormControl>
                            ))}
                          </>
                        )}

                      {/* Action Button configuration */}
                      {selectedElement.type === "action_button" && (
                        <>
                          <Divider />
                          <ActionButtonProperties
                            workspaceId={workspaceId}
                            props={selectedElement.props as never}
                            onChange={(patch) =>
                              handleUpdateElementProps(
                                selectedElement.tempId,
                                patch as never,
                              )
                            }
                          />
                        </>
                      )}

                      {/* Embedded Canvas configuration */}
                      {selectedElement.type === "embedded_canvas" && (
                        <EmbeddedCanvasConfig
                          element={selectedElement}
                          workspaceId={workspaceId}
                          canvasId={canvasId}
                          dataSources={dataSources}
                          viewFieldsCache={viewFieldsCache}
                          onUpdateProps={(props) =>
                            handleUpdateElementProps(
                              selectedElement.tempId,
                              props,
                            )
                          }
                        />
                      )}

                      {/* View Grid configuration */}
                      {selectedElement.type === "view_grid" && (
                        <ViewGridCanvasConfig
                          element={selectedElement}
                          dataSources={dataSources}
                          viewFieldsCache={viewFieldsCache}
                          onUpdateProps={(props) =>
                            handleUpdateElementProps(
                              selectedElement.tempId,
                              props,
                            )
                          }
                        />
                      )}

                      {selectedElement.type === "widget" && (
                        <WidgetCanvasConfig
                          element={selectedElement}
                          dataSources={dataSources}
                          onUpdateProps={(props) =>
                            handleUpdateElementProps(
                              selectedElement.tempId,
                              props,
                            )
                          }
                        />
                      )}

                      {selectedElement.type === "hierarchy" && (
                        <HierarchyProperties
                          props={
                            selectedElement.props as unknown as Record<
                              string,
                              unknown
                            >
                          }
                          dataSources={dataSources}
                          onChange={(props) =>
                            handleUpdateElementProps(
                              selectedElement.tempId,
                              props as Partial<CanvasElement["props"]>,
                            )
                          }
                        />
                      )}

                      {selectedElement.type === "related_records" && (
                        <RelatedRecordsProperties
                          props={
                            selectedElement.props as unknown as Record<
                              string,
                              unknown
                            >
                          }
                          dataSources={dataSources}
                          onChange={(props) =>
                            handleUpdateElementProps(
                              selectedElement.tempId,
                              props as Partial<CanvasElement["props"]>,
                            )
                          }
                        />
                      )}

                      {selectedElement.type === "calendar" && (
                        <CalendarCanvasConfig
                          element={selectedElement}
                          dataSources={dataSources}
                          viewFieldsCache={viewFieldsCache}
                          onUpdateProps={(props) =>
                            handleUpdateElementProps(
                              selectedElement.tempId,
                              props,
                            )
                          }
                        />
                      )}

                      {selectedElement.type === "kanban" && (
                        <KanbanCanvasConfig
                          element={selectedElement}
                          dataSources={dataSources}
                          viewFieldsCache={viewFieldsCache}
                          onUpdateProps={(props) =>
                            handleUpdateElementProps(
                              selectedElement.tempId,
                              props,
                            )
                          }
                        />
                      )}

                      {selectedElement.type === "timeline" && (
                        <TimelineCanvasConfig
                          element={selectedElement}
                          dataSources={dataSources}
                          viewFieldsCache={viewFieldsCache}
                          onUpdateProps={(props) =>
                            handleUpdateElementProps(
                              selectedElement.tempId,
                              props,
                            )
                          }
                        />
                      )}

                      {/* Field Type Configuration (for FIELD elements only) */}
                      {selectedElement.type === "field" && (
                        <>
                          <Divider />
                          <Typography level="title-sm">Field Type</Typography>

                          {/* Show detected type based on bound column */}
                          {(() => {
                            const viewId = getElementViewId(selectedElement);
                            const viewFields = viewId
                              ? viewFieldsCache[viewId] || []
                              : [];
                            const boundField = viewFields.find(
                              (f) =>
                                String(f.id) ===
                                selectedElement.data_binding?.field_path,
                            );
                            const columnType = boundField?.column?.data_type;

                            // Infer field type from column type
                            const inferFieldType = (
                              dataType?: string,
                            ): string => {
                              if (!dataType) return "text";
                              const dt = dataType.toLowerCase();
                              if (
                                dt.includes("int") ||
                                dt.includes("decimal") ||
                                dt.includes("float") ||
                                dt.includes("numeric")
                              )
                                return "number";
                              if (
                                dt.includes("datetime") ||
                                dt.includes("timestamp")
                              )
                                return "datetime";
                              if (dt.includes("date")) return "date";
                              if (dt.includes("bool") || dt.includes("bit"))
                                return "boolean";
                              if (
                                dt.includes("text") ||
                                (dt.includes("varchar") &&
                                  parseInt(dt.replace(/\D/g, "") || "0") > 500)
                              )
                                return "textarea";
                              return "text";
                            };

                            const detectedType = inferFieldType(columnType);
                            const currentType =
                              selectedElement.props.fieldType ||
                              selectedElement.props.variant;
                            const isOverridden =
                              currentType &&
                              currentType !== "auto" &&
                              currentType !== detectedType;

                            return (
                              <>
                                {boundField && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mb: 1,
                                    }}
                                  >
                                    <Typography
                                      level="body-sm"
                                      sx={{ color: "text.secondary" }}
                                    >
                                      Detected:
                                    </Typography>
                                    <Chip
                                      size="sm"
                                      variant="soft"
                                      color="neutral"
                                    >
                                      {detectedType.charAt(0).toUpperCase() +
                                        detectedType.slice(1)}
                                    </Chip>
                                    {columnType && (
                                      <Typography
                                        level="body-xs"
                                        sx={{ color: "text.tertiary" }}
                                      >
                                        ({columnType})
                                      </Typography>
                                    )}
                                  </Box>
                                )}

                                <FormControl>
                                  <FormLabel>
                                    {boundField
                                      ? "Override Type"
                                      : "Field Type"}
                                  </FormLabel>
                                  <Select
                                    value={currentType || "auto"}
                                    onChange={(_, value) => {
                                      const fieldType =
                                        value === "auto"
                                          ? undefined
                                          : (value as
                                              | "text"
                                              | "number"
                                              | "date"
                                              | "datetime"
                                              | "boolean"
                                              | "select"
                                              | "textarea"
                                              | "json");
                                      handleUpdateElementProps(
                                        selectedElement.tempId,
                                        {
                                          fieldType,
                                          variant: fieldType,
                                        },
                                      );
                                    }}
                                  >
                                    <Option value="auto">Auto-detect</Option>
                                    <Option value="text">Text</Option>
                                    <Option value="number">Number</Option>
                                    <Option value="date">Date</Option>
                                    <Option value="datetime">
                                      Date & Time
                                    </Option>
                                    <Option value="boolean">Checkbox</Option>
                                    <Option value="select">Select</Option>
                                    <Option value="textarea">Text Area</Option>
                                    <Option value="json">JSON</Option>
                                  </Select>
                                  <FormHelperText>
                                    {boundField
                                      ? "Override the auto-detected type if needed"
                                      : "Select the input type for this field"}
                                  </FormHelperText>
                                </FormControl>

                                {isOverridden && (
                                  <Alert
                                    size="sm"
                                    color="warning"
                                    variant="soft"
                                  >
                                    Type is overridden from {detectedType} to{" "}
                                    {currentType}
                                  </Alert>
                                )}
                              </>
                            );
                          })()}

                          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            <Checkbox
                              label="Read-only"
                              checked={!!selectedElement.props.readOnly}
                              onChange={(e) =>
                                handleUpdateElementProps(
                                  selectedElement.tempId,
                                  {
                                    readOnly: e.target.checked,
                                  },
                                )
                              }
                            />
                          </Stack>
                        </>
                      )}

                      {/* Advanced Properties - Collapsible Sections */}
                      <Divider sx={{ mt: 2 }} />
                      <Typography level="title-sm" sx={{ mt: 1 }}>
                        Advanced Options
                      </Typography>

                      <AccordionGroup size="sm" sx={{ mt: 1 }}>
                        {/* Visibility Rules - for ALL element types */}
                        <Accordion>
                          <AccordionSummary indicator={<ExpandMoreIcon />}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <RuleIcon sx={{ fontSize: 18 }} />
                              <Typography level="body-sm" fontWeight="md">
                                Visibility Rules
                              </Typography>
                              {selectedElement.visibilityRules?.conditions
                                ?.length ? (
                                <Chip size="sm" color="primary" variant="soft">
                                  {
                                    selectedElement.visibilityRules.conditions
                                      .length
                                  }
                                </Chip>
                              ) : null}
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails>
                            <VisibilityRulesEditor
                              rules={selectedElement.visibilityRules}
                              onChange={(rules) => {
                                setElements((prev) =>
                                  prev.map((el) =>
                                    el.tempId === selectedElement.tempId
                                      ? { ...el, visibilityRules: rules }
                                      : el,
                                  ),
                                );
                                setIsDirty(true);
                              }}
                              dataSources={dataSources}
                              viewFieldsCache={viewFieldsCache}
                            />
                          </AccordionDetails>
                        </Accordion>

                        {/* Auto-Fill Configuration - for FIELD elements only */}
                        {selectedElement.type === "field" && (
                          <Accordion>
                            <AccordionSummary indicator={<ExpandMoreIcon />}>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <AutoFixHighIcon sx={{ fontSize: 18 }} />
                                <Typography level="body-sm" fontWeight="md">
                                  Auto-Fill
                                </Typography>
                                {selectedElement.autoFill ? (
                                  <Chip
                                    size="sm"
                                    color="success"
                                    variant="soft"
                                  >
                                    On
                                  </Chip>
                                ) : null}
                              </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                              <AutoFillEditor
                                config={selectedElement.autoFill}
                                onChange={(config) => {
                                  setElements((prev) =>
                                    prev.map((el) =>
                                      el.tempId === selectedElement.tempId
                                        ? { ...el, autoFill: config }
                                        : el,
                                    ),
                                  );
                                  setIsDirty(true);
                                }}
                                dataSources={dataSources}
                                viewFieldsCache={viewFieldsCache}
                              />
                            </AccordionDetails>
                          </Accordion>
                        )}

                        {/* Validation Rules - for FIELD elements only */}
                        {selectedElement.type === "field" && (
                          <Accordion>
                            <AccordionSummary indicator={<ExpandMoreIcon />}>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <VerifiedIcon sx={{ fontSize: 18 }} />
                                <Typography level="body-sm" fontWeight="md">
                                  Validation Rules
                                </Typography>
                                {selectedElement.validationRules?.length ? (
                                  <Chip
                                    size="sm"
                                    color="warning"
                                    variant="soft"
                                  >
                                    {selectedElement.validationRules.length}
                                  </Chip>
                                ) : null}
                              </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                              <ValidationRulesEditor
                                rules={selectedElement.validationRules || []}
                                fieldType={
                                  (selectedElement.props.fieldType as string) ||
                                  "text"
                                }
                                onChange={(rules) => {
                                  setElements((prev) =>
                                    prev.map((el) =>
                                      el.tempId === selectedElement.tempId
                                        ? { ...el, validationRules: rules }
                                        : el,
                                    ),
                                  );
                                  setIsDirty(true);
                                }}
                              />
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </AccordionGroup>
                    </Stack>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.tertiary" }}
                      >
                        Select an element to edit its properties
                      </Typography>
                    </Box>
                  )}
                </TabPanel>

                <TabPanel value={1} sx={{ flex: 1, overflow: "auto", p: 2 }}>
                  <Stack spacing={2}>
                    <Button
                      size="sm"
                      variant="soft"
                      startDecorator={<DatasetIcon />}
                      onClick={handleAddDataSource}
                    >
                      Add Data Source
                    </Button>

                    <Typography
                      level="body-xs"
                      sx={{ color: "text.tertiary", mb: 1 }}
                    >
                      Data sources are created automatically when you bind
                      fields to views.
                    </Typography>

                    {dataSources.length === 0 ? (
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.tertiary" }}
                      >
                        No data sources yet. Select a view in field properties
                        to create one.
                      </Typography>
                    ) : (
                      dataSources.map((ds) => (
                        <Card key={ds.id} variant="outlined" size="sm">
                          <CardContent>
                            <Stack spacing={1}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Input
                                  variant="plain"
                                  value={ds.name}
                                  onChange={(e) =>
                                    handleUpdateDataSource(ds.id, {
                                      name: e.target.value,
                                    })
                                  }
                                  sx={{ fontWeight: "md" }}
                                />
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  color="danger"
                                  onClick={() => handleRemoveDataSource(ds.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>

                              <FormControl size="sm">
                                <FormLabel>View</FormLabel>
                                <Select
                                  value={ds.view_id ? String(ds.view_id) : ""}
                                  onChange={(_, value) =>
                                    handleUpdateDataSource(ds.id, {
                                      view_id: value || undefined,
                                    })
                                  }
                                  placeholder="Select view"
                                >
                                  {views.map((v) => (
                                    <Option key={v.id} value={String(v.id)}>
                                      {v.name}
                                    </Option>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </Stack>
                </TabPanel>
              </Tabs>
            </Sheet>
          )}
        </Box>
      </Box>

      {/* Drag overlay for toolbox items */}
      <DragOverlay>
        {draggedItemType && (
          <Box
            sx={{
              p: 1,
              px: 2,
              bgcolor: "primary.softBg",
              borderRadius: "md",
              border: "1px solid",
              borderColor: "primary.500",
              boxShadow: "lg",
              opacity: 0.9,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {draggedItemType.icon}
              <Typography level="body-sm" fontWeight="md">
                {draggedItemType.label}
              </Typography>
            </Stack>
          </Box>
        )}
      </DragOverlay>

      {/* MyVault Agent Chat Widget */}
      <FloatingChatWidget workspaceId={workspaceId} />
    </DndContext>
  );
}

// Helper function to render element preview - matches CanvasRenderer for WYSIWYG
export function renderElementPreview(
  element: CanvasBuilderElement,
  allElements: CanvasBuilderElement[],
  selectedElementId: string | null,
  hoveredElementId: string | null,
  onNavigateToChildCanvas: (childCanvasId: string | number) => void,
  renderNestedElement: (element: CanvasBuilderElement) => React.ReactNode,
): React.ReactNode {
  const variant = getCanvasBuilderContainerVariant(element);
  const label = element.props.label as string;

  // Get children for container elements
  const children = getCanvasBuilderChildren(allElements, element.tempId);

  // Handle embedded_canvas type before variant switch
  if (element.type === "embedded_canvas") {
    const childCanvasId = (
      element.props as unknown as { childCanvasId?: string | number }
    ).childCanvasId;
    const parentKeyField = (
      element.props as unknown as { parentKeyField?: string }
    ).parentKeyField;
    const childFilterField = (
      element.props as unknown as { childFilterField?: string }
    ).childFilterField;

    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 120,
          display: "flex",
          flexDirection: "column",
          borderRadius: "sm",
          overflow: "hidden",
          border: "2px solid",
          borderColor: childCanvasId ? "primary.300" : "warning.300",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1,
            bgcolor: childCanvasId ? "primary.softBg" : "warning.softBg",
            borderBottom: "1px solid",
            borderColor: childCanvasId ? "primary.200" : "warning.200",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DashboardIcon
              sx={{
                fontSize: 18,
                color: childCanvasId ? "primary.500" : "warning.500",
              }}
            />
            <Typography level="body-sm" fontWeight="md">
              {label || "Embedded Canvas"}
            </Typography>
          </Box>
          {childCanvasId && onNavigateToChildCanvas && (
            <Tooltip title="Edit child canvas">
              <IconButton
                size="sm"
                variant="soft"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigateToChildCanvas(childCanvasId);
                }}
              >
                <EditNoteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {/* Content */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.level1",
            p: 2,
          }}
        >
          {childCanvasId ? (
            <>
              {parentKeyField && childFilterField ? (
                <Chip color="success" variant="soft" size="sm">
                  Linked: {parentKeyField} → {childFilterField}
                </Chip>
              ) : (
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Canvas ID: {childCanvasId}
                </Typography>
              )}
            </>
          ) : (
            <Typography level="body-xs" sx={{ color: "warning.600" }}>
              Not configured - select a child canvas in properties
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  if (element.type === "kanban") {
    const config = element.props as unknown as {
      title?: string;
      data_source_alias?: string;
      status_field?: string;
      columns?: string[];
      kanban_config?: { status_field?: string; columns?: string[] };
    };
    const statusField =
      config.status_field || config.kanban_config?.status_field;
    const columns = config.columns || config.kanban_config?.columns || [];

    return (
      <Sheet
        variant="soft"
        data-testid="kanban-editor-preview"
        sx={{
          height: "100%",
          minHeight: 120,
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        <Typography level="title-sm">
          {config.title || label || "Kanban board"}
        </Typography>
        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
          Data source: {config.data_source_alias || "main"}
        </Typography>
        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
          Status field: {statusField || "Not configured"}
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap">
          {columns.length > 0 ? (
            columns.map((column) => (
              <Chip key={column} size="sm" variant="outlined">
                {column}
              </Chip>
            ))
          ) : (
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              No status columns configured
            </Typography>
          )}
        </Stack>
      </Sheet>
    );
  }

  if (element.type === "view_grid") {
    const title = (element.props as unknown as { title?: string }).title;
    const dataSourceAlias = (
      element.props as unknown as { data_source_alias?: string }
    ).data_source_alias;

    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 160,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "sm",
          border: "2px dashed",
          borderColor: "primary.300",
          bgcolor: "primary.softBg",
          p: 2,
          gap: 1,
          textAlign: "center",
        }}
      >
        <GridOnIcon sx={{ fontSize: 32, color: "primary.500" }} />
        <Typography level="body-sm" fontWeight="md">
          {title || label || "View Grid"}
        </Typography>
        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
          Browse records from {dataSourceAlias || "main"}
        </Typography>
      </Box>
    );
  }

  // Handle element types directly (for simplified toolbox elements)
  if (element.type === "text") {
    const level = element.props.level as number | undefined;
    const renderAsHtml = element.props.renderAsHtml as boolean;
    const textStyles = {
      color: element.props.textColor || undefined,
      textAlign: element.props.textAlign || undefined,
      fontWeight: element.props.fontWeight || undefined,
    };

    if (renderAsHtml) {
      return (
        <Box
          sx={{
            ...textStyles,
            p: 1,
            border: "1px dashed",
            borderColor: "neutral.outlinedBorder",
            borderRadius: "sm",
          }}
          dangerouslySetInnerHTML={{ __html: label || "<p>HTML content</p>" }}
        />
      );
    }

    if (level) {
      const typographyLevel =
        level === 1
          ? "h1"
          : level === 2
            ? "h2"
            : level === 3
              ? "h3"
              : level === 4
                ? "h4"
                : level === 5
                  ? "title-lg"
                  : "title-md";
      return (
        <Typography level={typographyLevel} sx={textStyles}>
          {label || "Heading"}
        </Typography>
      );
    }

    // Default paragraph text
    return (
      <Typography level="body-md" sx={textStyles}>
        {label || "Text"}
      </Typography>
    );
  }

  if (element.type === "field") {
    // Show a generic data field preview - actual type is determined at runtime
    // Label is auto-populated from field display name when bound
    const isBound = !!element.data_binding?.field_path;
    const fieldLabel = label || (isBound ? "Bound Field" : "Data Field");
    const placeholder =
      element.props.placeholder ||
      (isBound ? "Enter value..." : "Select a column to bind...");

    return (
      <FormControl>
        <FormLabel>{fieldLabel}</FormLabel>
        <Input
          size="sm"
          placeholder={placeholder}
          disabled
          startDecorator={
            <DatasetIcon sx={{ fontSize: 16, color: "neutral.500" }} />
          }
        />
      </FormControl>
    );
  }

  if (element.type === "divider") {
    return <Divider sx={{ my: 1 }} />;
  }

  if (element.type === "image") {
    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.level1",
          borderRadius: "sm",
          border: "1px dashed",
          borderColor: "neutral.outlinedBorder",
        }}
      >
        <Stack alignItems="center" spacing={0.5}>
          <ImageIcon sx={{ fontSize: 32, color: "neutral.400" }} />
          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
            {label || "Image"}
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Fallback to variant-based switch for backward compatibility with existing canvases
  switch (variant) {
    case "text-input":
    case "text":
      return (
        <FormControl>
          <FormLabel>{label}</FormLabel>
          <Input
            size="sm"
            placeholder={element.props.placeholder || "Enter text..."}
            disabled
          />
        </FormControl>
      );
    case "number-input":
    case "number":
      return (
        <FormControl>
          <FormLabel>{label}</FormLabel>
          <Input
            size="sm"
            type="number"
            placeholder={element.props.placeholder || "0"}
            disabled
          />
        </FormControl>
      );
    case "date":
      return (
        <FormControl>
          <FormLabel>{label}</FormLabel>
          <Input size="sm" type="date" disabled />
        </FormControl>
      );
    case "textarea":
      return (
        <FormControl sx={{ height: "100%" }}>
          <FormLabel>{label}</FormLabel>
          <Box
            component="textarea"
            disabled
            placeholder={element.props.placeholder || "Enter text..."}
            sx={{
              flex: 1,
              minHeight: 60,
              p: 1,
              borderRadius: "sm",
              border: "1px solid",
              borderColor: "neutral.outlinedBorder",
              bgcolor: "background.surface",
              fontSize: "sm",
              resize: "none",
              "&::placeholder": { color: "neutral.500" },
            }}
          />
        </FormControl>
      );
    case "checkbox":
    case "check":
      return (
        <FormControl>
          <Checkbox label={label || "Option"} disabled />
        </FormControl>
      );
    case "select":
      return (
        <FormControl>
          <FormLabel>{label}</FormLabel>
          <Select size="sm" placeholder="Select..." disabled>
            <Option value="1">Option 1</Option>
          </Select>
        </FormControl>
      );
    case "section":
      return (
        <Box
          sx={{
            p: 1.5,
            borderRadius: "sm",
            bgcolor: "background.level1",
            border: "1px solid",
            borderColor: "neutral.outlinedBorder",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography level="title-sm" sx={{ mb: 1 }}>
            {label || "Section"}
          </Typography>
          {children.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px dashed",
                borderColor: "neutral.outlinedBorder",
                borderRadius: "sm",
                minHeight: 60,
              }}
            >
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                Drop elements here
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: "repeat(12, 1fr)",
                gap: 1,
                alignContent: "start",
              }}
            >
              {children
                .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                .map((child) => renderNestedElement(child))}
            </Box>
          )}
        </Box>
      );
    case "card":
      return (
        <Card variant="outlined" sx={{ height: "100%", overflow: "hidden" }}>
          <CardContent
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              p: 1.5,
            }}
          >
            <Typography level="title-sm" sx={{ mb: 1 }}>
              {label || "Card"}
            </Typography>
            {children.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px dashed",
                  borderColor: "neutral.outlinedBorder",
                  borderRadius: "sm",
                  minHeight: 60,
                }}
              >
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Drop elements here
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: `repeat(${getCanvasBuilderInternalGridColumns(element)}, 1fr)`,
                  gap: 1,
                  alignContent: "start",
                }}
              >
                {children
                  .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                  .map((child) => renderNestedElement(child))}
              </Box>
            )}
          </CardContent>
        </Card>
      );
    case "columns": {
      // Get column widths from container config
      const columnWidths = (
        element.props as unknown as { columnWidths?: string[] }
      ).columnWidths || ["1fr", "1fr"];

      return (
        <Box
          sx={{
            p: 1.5,
            borderRadius: "sm",
            bgcolor: "background.level1",
            border: "1px solid",
            borderColor: "neutral.outlinedBorder",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography level="title-sm" sx={{ mb: 1 }}>
            {label || "Columns"}
          </Typography>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "row",
              gap: 2,
            }}
          >
            {columnWidths.map((width, index) => {
              const columnId = `col-${index}`;
              const columnChildren = children.filter(
                (c) => c.parentPanelId === columnId,
              );
              // Parse "1fr" -> 1, "2fr" -> 2
              const flexValue = parseFloat(width.replace("fr", "")) || 1;

              return (
                <Box
                  key={columnId}
                  data-canvas-parent-id={element.tempId}
                  data-canvas-panel-id={columnId}
                  sx={{
                    flex: flexValue,
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                  }}
                >
                  {columnChildren.length === 0 ? (
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px dashed",
                        borderColor: "neutral.outlinedBorder",
                        borderRadius: "sm",
                        minHeight: 60,
                      }}
                    >
                      <Typography
                        level="body-xs"
                        sx={{ color: "text.tertiary" }}
                      >
                        Column {index + 1}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(12, 1fr)",
                        gap: 1,
                        alignContent: "start",
                      }}
                    >
                      {columnChildren
                        .sort(
                          (a, b) =>
                            (a.displayOrder || 0) - (b.displayOrder || 0),
                        )
                        .map((child) => renderNestedElement(child))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    }
    case "tabs": {
      // Get panels from container config
      const panels = (
        element.props as unknown as {
          panels?: Array<{ id: string; label: string }>;
        }
      ).panels || [
        { id: "tab1", label: "Tab 1" },
        { id: "tab2", label: "Tab 2" },
      ];
      return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Tabs
            defaultValue={panels[0]?.id || 0}
            sx={{ bgcolor: "transparent" }}
          >
            <CanvasTabHeaders
              ownerId={element.id || element.tempId}
              panels={panels}
            />
            {panels.map((panel) => {
              const panelChildren = children.filter(
                (c) => c.parentPanelId === panel.id,
              );
              return (
                <TabPanel
                  key={panel.id}
                  data-canvas-parent-id={element.tempId}
                  data-canvas-panel-id={panel.id}
                  value={panel.id}
                  sx={{ p: 0, flex: 1 }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      p: 1.5,
                      border: "1px solid",
                      borderColor: "neutral.outlinedBorder",
                      borderTop: "none",
                      borderRadius: "0 0 sm sm",
                    }}
                  >
                    {panelChildren.length === 0 ? (
                      <Box
                        sx={{
                          height: "100%",
                          minHeight: 60,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px dashed",
                          borderColor: "neutral.outlinedBorder",
                          borderRadius: "sm",
                        }}
                      >
                        <Typography
                          level="body-xs"
                          sx={{ color: "text.tertiary" }}
                        >
                          Drop elements here
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(12, 1fr)",
                          gap: 1,
                          alignContent: "start",
                        }}
                      >
                        {panelChildren
                          .sort(
                            (a, b) =>
                              (a.displayOrder || 0) - (b.displayOrder || 0),
                          )
                          .map((child) => renderNestedElement(child))}
                      </Box>
                    )}
                  </Box>
                </TabPanel>
              );
            })}
          </Tabs>
        </Box>
      );
    }
    case "label":
    case "heading":
    case "html":
    case "icon":
    case "spacer": {
      // Handle text variants for static text elements
      const textVariant = element.props.textVariant as string;
      const textStyles = {
        color: element.props.textColor || undefined,
        textAlign: element.props.textAlign || undefined,
        fontWeight: element.props.fontWeight || undefined,
      };

      switch (textVariant) {
        case "heading": {
          const headingLevel = (element.props.level as number) || 2;
          const typographyLevel =
            headingLevel === 1
              ? "h1"
              : headingLevel === 2
                ? "h2"
                : headingLevel === 3
                  ? "h3"
                  : headingLevel === 4
                    ? "h4"
                    : headingLevel === 5
                      ? "title-lg"
                      : "title-md";
          return (
            <Typography level={typographyLevel} sx={textStyles}>
              {label || "Heading"}
            </Typography>
          );
        }
        case "html":
          return (
            <Box
              sx={{
                ...textStyles,
                p: 1,
                border: "1px dashed",
                borderColor: "neutral.outlinedBorder",
                borderRadius: "sm",
              }}
              dangerouslySetInnerHTML={{
                __html:
                  element.props.htmlContent || label || "<p>HTML content</p>",
              }}
            />
          );
        case "icon": {
          const iconName = element.props.iconName as string;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: element.props.textAlign || "center",
                height: "100%",
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Icon: {iconName || "Star"}
                </Typography>
              </Box>
            </Box>
          );
        }
        case "spacer": {
          const spacerHeight = (element.props.spacerHeight as number) || 24;
          return (
            <Box
              sx={{
                height: spacerHeight,
                minHeight: spacerHeight,
                bgcolor: "background.level1",
                borderRadius: "sm",
              }}
            />
          );
        }
        default:
          // Default label rendering
          return (
            <Typography
              level={
                element.props.size === "lg"
                  ? "h4"
                  : element.props.size === "sm"
                    ? "body-sm"
                    : "body-md"
              }
              sx={textStyles}
            >
              {label || "Label"}
            </Typography>
          );
      }
    }
    case "divider":
      return <Divider sx={{ my: 1 }} />;
    case "image":
      return (
        <Box
          sx={{
            height: "100%",
            minHeight: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.level1",
            borderRadius: "sm",
            border: "1px dashed",
            borderColor: "neutral.outlinedBorder",
          }}
        >
          <Stack alignItems="center" spacing={0.5}>
            <ImageIcon sx={{ fontSize: 32, color: "neutral.400" }} />
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              {label || "Image"}
            </Typography>
          </Stack>
        </Box>
      );
    default:
      return (
        <FormControl>
          <FormLabel>{label}</FormLabel>
          <Input size="sm" placeholder={element.props.placeholder} disabled />
        </FormControl>
      );
  }
}

/**
 * EmbeddedCanvasConfig - Configuration panel for embedded canvas elements.
 */
interface EmbeddedCanvasConfigProps {
  element: CanvasBuilderElement;
  workspaceId: string;
  canvasId: string | null;
  dataSources: CanvasDataSource[];
  viewFieldsCache: Record<string, ViewField[]>;
  onUpdateProps: (props: Partial<CanvasElement["props"]>) => void;
}

function EmbeddedCanvasConfig({
  element,
  workspaceId,
  canvasId,
  dataSources,
  viewFieldsCache,
  onUpdateProps,
}: EmbeddedCanvasConfigProps) {
  const { canvases: availableCanvases, loading: loadingCanvases } =
    useAvailableCanvasesForEmbedding(workspaceId, canvasId);

  // Get current embedding config from props
  const childCanvasId = (
    element.props as unknown as { childCanvasId?: string | number }
  ).childCanvasId;
  const parentDataSourceAlias = (
    element.props as unknown as { parentDataSourceAlias?: string }
  ).parentDataSourceAlias;
  const parentKeyField = (
    element.props as unknown as { parentKeyField?: string }
  ).parentKeyField;
  const childDataSourceAlias = (
    element.props as unknown as { childDataSourceAlias?: string }
  ).childDataSourceAlias;
  const childFilterField = (
    element.props as unknown as { childFilterField?: string }
  ).childFilterField;

  // Find selected child canvas info
  const selectedCanvas = availableCanvases.find(
    (c) => String(c.id) === String(childCanvasId),
  );

  // Get fields for selected parent data source
  const selectedParentDs = dataSources.find(
    (ds) =>
      ds.name === parentDataSourceAlias || ds.id === parentDataSourceAlias,
  );
  const parentViewId = selectedParentDs?.view_id
    ? String(selectedParentDs.view_id)
    : "";
  const parentFields = parentViewId ? viewFieldsCache[parentViewId] || [] : [];

  return (
    <>
      <Divider />
      <Typography level="title-sm">Embedded Canvas</Typography>

      {/* Child Canvas Selector */}
      <FormControl>
        <FormLabel>Child Canvas</FormLabel>
        {loadingCanvases ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size="sm" />
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
              Loading canvases...
            </Typography>
          </Box>
        ) : availableCanvases.length === 0 ? (
          <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
            No other canvases available for embedding
          </Typography>
        ) : (
          <Select
            value={childCanvasId ? String(childCanvasId) : ""}
            onChange={(_, value) =>
              onUpdateProps({
                childCanvasId: value || undefined,
              } as unknown as Partial<CanvasElement["props"]>)
            }
            placeholder="Select a canvas to embed"
          >
            <Option value="">None</Option>
            {availableCanvases.map((canvas) => (
              <Option key={canvas.id} value={String(canvas.id)}>
                {canvas.name} ({canvasTypeFromDefinition(canvas)})
              </Option>
            ))}
          </Select>
        )}
      </FormControl>

      {childCanvasId && (
        <>
          <Divider />
          <Typography level="title-sm">Parent-Child Linking</Typography>

          {/* Parent Data Source */}
          <FormControl>
            <FormLabel>Parent Data Source</FormLabel>
            <Select
              value={parentDataSourceAlias || ""}
              onChange={(_, value) =>
                onUpdateProps({
                  parentDataSourceAlias: value || undefined,
                } as unknown as Partial<CanvasElement["props"]>)
              }
              placeholder="Select parent data source"
            >
              <Option value="">None</Option>
              {dataSources.map((ds) => (
                <Option key={ds.id} value={ds.name || ds.id}>
                  {ds.name}
                </Option>
              ))}
            </Select>
          </FormControl>

          {/* Parent Key Field */}
          <FormControl>
            <FormLabel>Parent Key Field</FormLabel>
            {parentFields.length > 0 ? (
              <Select
                value={parentKeyField || ""}
                onChange={(_, value) =>
                  onUpdateProps({
                    parentKeyField: value || undefined,
                  } as unknown as Partial<CanvasElement["props"]>)
                }
                placeholder="Select key field"
              >
                <Option value="">None</Option>
                {parentFields.map((field) => (
                  <Option key={field.id} value={String(field.id)}>
                    {field.display_name ||
                      field.column?.display_name ||
                      field.alias ||
                      field.column?.column_name ||
                      "Unnamed Field"}
                  </Option>
                ))}
              </Select>
            ) : (
              <Input
                value={parentKeyField || ""}
                onChange={(e) =>
                  onUpdateProps({
                    parentKeyField: e.target.value || undefined,
                  } as unknown as Partial<CanvasElement["props"]>)
                }
                placeholder="e.g., id or person_id"
              />
            )}
          </FormControl>

          {/* Child Data Source */}
          <FormControl>
            <FormLabel>Child Data Source Alias</FormLabel>
            {selectedCanvas?.data_sources &&
            selectedCanvas.data_sources.length > 0 ? (
              <Select
                value={childDataSourceAlias || ""}
                onChange={(_, value) =>
                  onUpdateProps({
                    childDataSourceAlias: value || undefined,
                  } as unknown as Partial<CanvasElement["props"]>)
                }
                placeholder="Select child data source"
              >
                <Option value="">None</Option>
                {selectedCanvas.data_sources.map((ds) => (
                  <Option key={ds.alias} value={ds.alias}>
                    {ds.alias}
                    {ds.view_name && ` (${ds.view_name})`}
                  </Option>
                ))}
              </Select>
            ) : (
              <Input
                value={childDataSourceAlias || ""}
                onChange={(e) =>
                  onUpdateProps({
                    childDataSourceAlias: e.target.value || undefined,
                  } as unknown as Partial<CanvasElement["props"]>)
                }
                placeholder="e.g., contacts"
              />
            )}
          </FormControl>

          {/* Child Filter Field */}
          <FormControl>
            <FormLabel>Child Filter Field</FormLabel>
            <Input
              value={childFilterField || ""}
              onChange={(e) =>
                onUpdateProps({
                  childFilterField: e.target.value || undefined,
                } as unknown as Partial<CanvasElement["props"]>)
              }
              placeholder="e.g., person_id or parent_id"
            />
            <Typography
              level="body-xs"
              sx={{ color: "text.tertiary", mt: 0.5 }}
            >
              The field in the child canvas view used to filter records
            </Typography>
          </FormControl>

          {parentKeyField && childFilterField && (
            <Chip
              color="success"
              variant="soft"
              sx={{ alignSelf: "flex-start" }}
            >
              Linked: {parentKeyField} → {childFilterField}
            </Chip>
          )}
        </>
      )}
    </>
  );
}

interface KanbanCanvasConfigProps {
  element: CanvasBuilderElement;
  dataSources: CanvasDataSource[];
  viewFieldsCache: Record<string, ViewField[]>;
  onUpdateProps: (props: Partial<CanvasElement["props"]>) => void;
}

function KanbanCanvasConfig({
  element,
  dataSources,
  viewFieldsCache,
  onUpdateProps,
}: KanbanCanvasConfigProps) {
  const props = element.props as unknown as {
    title?: string;
    data_source_alias?: string;
    status_field?: string;
    columns?: string[];
    title_field?: string;
    subtitle_field?: string;
  };
  const alias = String(props.data_source_alias || "");
  const source = dataSources.find(
    (candidate) => candidate.id === alias || candidate.name === alias,
  );
  const fields = source?.view_id
    ? viewFieldsCache[String(source.view_id)] || []
    : [];
  const choices = fields
    .filter((field) => field.visible !== false)
    .map((field) => ({
      value:
        field.alias ||
        field.column?.column_name ||
        String(field.column_id || field.id),
      label:
        field.display_name ||
        field.alias ||
        field.column?.column_name ||
        String(field.id),
    }));
  const mappings = [
    ["status_field", "Status field", true],
    ["title_field", "Card title", true],
    ["subtitle_field", "Card subtitle", false],
  ] as const;
  const [columnsText, setColumnsText] = React.useState(
    (props.columns || []).join(", "),
  );
  return (
    <>
      <Divider />
      <Typography level="title-sm">Kanban board</Typography>
      <FormControl>
        <FormLabel>Title</FormLabel>
        <Input
          size="sm"
          value={String(props.title || "")}
          onChange={(event) => onUpdateProps({ title: event.target.value })}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Data Source</FormLabel>
        <Select
          value={alias || null}
          onChange={(_, value) =>
            onUpdateProps({ data_source_alias: value || "" })
          }
        >
          {dataSources
            .filter((candidate) => candidate.view_id)
            .map((candidate) => (
              <Option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </Option>
            ))}
        </Select>
      </FormControl>
      {mappings.map(([key, label, required]) => (
        <FormControl key={key}>
          <FormLabel>{label}</FormLabel>
          <Select
            value={String(props[key] || "")}
            onChange={(_, value) => onUpdateProps({ [key]: value || "" })}
            placeholder={required ? "Required" : "Optional"}
          >
            {!required && <Option value="">None</Option>}
            {choices.map((choice) => (
              <Option key={choice.value} value={choice.value}>
                {choice.label}
              </Option>
            ))}
          </Select>
        </FormControl>
      ))}
      <FormControl>
        <FormLabel>Columns (status values, in order)</FormLabel>
        <Input
          size="sm"
          value={columnsText}
          placeholder="Applied, Interview, Offer"
          onChange={(event) => {
            setColumnsText(event.target.value);
            onUpdateProps({
              columns: event.target.value
                .split(",")
                .map((column) => column.trim())
                .filter(Boolean),
            } as unknown as Partial<CanvasElement["props"]>);
          }}
        />
        <FormHelperText>
          One lane per value, left to right. Rows with any other status go to an
          Other lane.
        </FormHelperText>
      </FormControl>
      {fields.length === 0 && (
        <FormHelperText>
          Select a view data source to map its fields.
        </FormHelperText>
      )}
    </>
  );
}

function CalendarCanvasConfig({
  element,
  dataSources,
  viewFieldsCache,
  onUpdateProps,
}: KanbanCanvasConfigProps) {
  const props = element.props as unknown as {
    title?: string;
    data_source_alias?: string;
    date_field?: string;
    end_date_field?: string;
    title_field?: string;
    default_view?: string;
  };
  const alias = String(props.data_source_alias || "");
  const source = dataSources.find(
    (candidate) => candidate.id === alias || candidate.name === alias,
  );
  const fields = source?.view_id
    ? viewFieldsCache[String(source.view_id)] || []
    : [];
  const choices = fields
    .filter((field) => field.visible !== false)
    .map((field) => ({
      value:
        field.alias ||
        field.column?.column_name ||
        String(field.column_id || field.id),
      label:
        field.display_name ||
        field.alias ||
        field.column?.column_name ||
        String(field.id),
    }));
  const mappings = [
    ["date_field", "Date field", true],
    ["end_date_field", "End date field", false],
    ["title_field", "Event title", true],
  ] as const;
  return (
    <>
      <Divider />
      <Typography level="title-sm">Calendar</Typography>
      <FormControl>
        <FormLabel>Title</FormLabel>
        <Input
          size="sm"
          value={String(props.title || "")}
          onChange={(event) => onUpdateProps({ title: event.target.value })}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Data Source</FormLabel>
        <Select
          value={alias || null}
          onChange={(_, value) =>
            onUpdateProps({ data_source_alias: value || "" })
          }
        >
          {dataSources
            .filter((candidate) => candidate.view_id)
            .map((candidate) => (
              <Option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </Option>
            ))}
        </Select>
      </FormControl>
      {mappings.map(([key, label, required]) => (
        <FormControl key={key}>
          <FormLabel>{label}</FormLabel>
          <Select
            value={String(props[key] || "")}
            onChange={(_, value) => onUpdateProps({ [key]: value || "" })}
            placeholder={required ? "Required" : "Optional"}
          >
            {!required && <Option value="">None</Option>}
            {choices.map((choice) => (
              <Option key={choice.value} value={choice.value}>
                {choice.label}
              </Option>
            ))}
          </Select>
        </FormControl>
      ))}
      <FormControl>
        <FormLabel>Opens in</FormLabel>
        <Select
          value={props.default_view === "week" ? "week" : "month"}
          onChange={(_, value) =>
            onUpdateProps({
              default_view: value === "week" ? "week" : "month",
            } as unknown as Partial<CanvasElement["props"]>)
          }
        >
          <Option value="month">Month view</Option>
          <Option value="week">Week view</Option>
        </Select>
      </FormControl>
      {fields.length === 0 && (
        <FormHelperText>
          Select a view data source to map its fields.
        </FormHelperText>
      )}
    </>
  );
}

interface TimelineCanvasConfigProps {
  element: CanvasBuilderElement;
  dataSources: CanvasDataSource[];
  viewFieldsCache: Record<string, ViewField[]>;
  onUpdateProps: (props: Partial<CanvasElement["props"]>) => void;
}

function TimelineCanvasConfig({
  element,
  dataSources,
  viewFieldsCache,
  onUpdateProps,
}: TimelineCanvasConfigProps) {
  const props = element.props;
  const alias = String(props.data_source_alias || "");
  const source = dataSources.find(
    (candidate) => candidate.id === alias || candidate.name === alias,
  );
  const fields = source?.view_id
    ? viewFieldsCache[String(source.view_id)] || []
    : [];
  const choices = fields
    .filter((field) => field.visible !== false)
    .map((field) => ({
      value:
        field.alias ||
        field.column?.column_name ||
        String(field.column_id || field.id),
      label:
        field.display_name ||
        field.alias ||
        field.column?.column_name ||
        String(field.id),
    }));
  const mappings = [
    ["label_field", "Stage label"],
    ["status_field", "Status"],
    ["date_field", "Date"],
    ["description_field", "Description"],
    ["order_field", "Stage order"],
  ] as const;
  return (
    <>
      <Divider />
      <Typography level="title-sm">Timeline</Typography>
      <FormControl>
        <FormLabel>Title</FormLabel>
        <Input
          size="sm"
          value={String(props.title || "")}
          onChange={(event) => onUpdateProps({ title: event.target.value })}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Data Source</FormLabel>
        <Select
          value={alias || null}
          onChange={(_, value) =>
            onUpdateProps({ data_source_alias: value || "" })
          }
        >
          {dataSources
            .filter((candidate) => candidate.view_id)
            .map((candidate) => (
              <Option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </Option>
            ))}
        </Select>
      </FormControl>
      {mappings.map(([key, label]) => (
        <FormControl key={key}>
          <FormLabel>{label}</FormLabel>
          <Select
            value={String(props[key] || "")}
            onChange={(_, value) => onUpdateProps({ [key]: value || "" })}
            placeholder={key === "label_field" ? "Required" : "Optional"}
          >
            {key !== "label_field" && <Option value="">None</Option>}
            {choices.map((choice) => (
              <Option key={choice.value} value={choice.value}>
                {choice.label}
              </Option>
            ))}
          </Select>
        </FormControl>
      ))}
      {fields.length === 0 && (
        <FormHelperText>
          Select a view data source to map its fields.
        </FormHelperText>
      )}
    </>
  );
}

interface ViewGridCanvasConfigProps {
  element: CanvasBuilderElement;
  dataSources: CanvasDataSource[];
  viewFieldsCache: Record<string, ViewField[]>;
  onUpdateProps: (props: Partial<CanvasElement["props"]>) => void;
}

function ViewGridCanvasConfig({
  element,
  dataSources,
  viewFieldsCache,
  onUpdateProps,
}: ViewGridCanvasConfigProps) {
  const props = element.props as unknown as {
    title?: string;
    data_source_alias?: string;
    page_size?: number;
    enable_search?: boolean;
    enable_sort?: boolean;
    enable_pagination?: boolean;
    allow_create?: boolean;
    allow_edit?: boolean;
    allow_delete?: boolean;
    visible_columns?: string[];
    filter_controls?: CanvasViewGridFilterControl[];
  };
  const dataSourceAlias = props.data_source_alias || "main";
  const selectedDataSource = dataSources.find(
    (ds) => ds.name === dataSourceAlias || ds.id === dataSourceAlias,
  );
  const selectedViewId = selectedDataSource?.view_id
    ? String(selectedDataSource.view_id)
    : "";
  const viewFields = selectedViewId
    ? viewFieldsCache[selectedViewId] || []
    : [];
  const visibleColumns = props.visible_columns || [];
  const columnListValue = visibleColumns.join(", ");
  const fieldOptions = viewFields
    .filter((field) => field.visible !== false)
    .map((field) => ({
      key:
        field.alias ||
        field.column?.column_name ||
        String(field.column_id || field.id),
      label:
        field.display_name ||
        field.column?.display_name ||
        field.alias ||
        field.column?.column_name ||
        "Unnamed Field",
    }));

  return (
    <>
      <Divider />
      <Typography level="title-sm">View Grid</Typography>

      <FormControl>
        <FormLabel>Title</FormLabel>
        <Input
          size="sm"
          value={props.title || ""}
          onChange={(e) =>
            onUpdateProps({
              title: e.target.value || undefined,
            } as unknown as Partial<CanvasElement["props"]>)
          }
          placeholder="Browse records"
        />
      </FormControl>

      <FormControl>
        <FormLabel>Data Source</FormLabel>
        <Select
          value={dataSourceAlias}
          onChange={(_, value) =>
            onUpdateProps({
              data_source_alias: value || "main",
            } as unknown as Partial<CanvasElement["props"]>)
          }
          placeholder="Select view data source"
        >
          {dataSources.length === 0 ? (
            <Option value="main" disabled>
              No data sources configured
            </Option>
          ) : (
            dataSources.map((ds) => (
              <Option key={ds.id} value={ds.name || ds.id}>
                {ds.name}
                {ds.view_id ? ` (view ${ds.view_id})` : " (no view)"}
              </Option>
            ))
          )}
        </Select>
        <FormHelperText>
          View grids browse the full view row set with server-side paging,
          sorting, and search.
        </FormHelperText>
      </FormControl>

      <FormControl>
        <FormLabel>Page Size</FormLabel>
        <Select
          value={String(props.page_size || 25)}
          onChange={(_, value) =>
            onUpdateProps({
              page_size: Number(value || 25),
            } as unknown as Partial<CanvasElement["props"]>)
          }
        >
          {[10, 25, 50, 100].map((size) => (
            <Option key={size} value={String(size)}>
              {size}
            </Option>
          ))}
        </Select>
      </FormControl>

      <Stack spacing={1}>
        <FormControl
          orientation="horizontal"
          sx={{ justifyContent: "space-between" }}
        >
          <FormLabel>Search across pages</FormLabel>
          <Switch
            checked={props.enable_search !== false}
            onChange={(e) =>
              onUpdateProps({
                enable_search: e.target.checked,
              } as unknown as Partial<CanvasElement["props"]>)
            }
          />
        </FormControl>
        <FormControl
          orientation="horizontal"
          sx={{ justifyContent: "space-between" }}
        >
          <FormLabel>Server-side sorting</FormLabel>
          <Switch
            checked={props.enable_sort !== false}
            onChange={(e) =>
              onUpdateProps({
                enable_sort: e.target.checked,
              } as unknown as Partial<CanvasElement["props"]>)
            }
          />
        </FormControl>
        <FormControl
          orientation="horizontal"
          sx={{ justifyContent: "space-between" }}
        >
          <FormLabel>Pagination</FormLabel>
          <Switch
            checked={props.enable_pagination !== false}
            onChange={(e) =>
              onUpdateProps({
                enable_pagination: e.target.checked,
              } as unknown as Partial<CanvasElement["props"]>)
            }
          />
        </FormControl>
      </Stack>

      <Divider />
      <Typography level="body-sm" fontWeight="md">
        Row Actions
      </Typography>
      <Stack spacing={1}>
        {[
          ["allow_create", "Allow create"],
          ["allow_edit", "Allow edit"],
          ["allow_delete", "Allow delete"],
        ].map(([key, label]) => (
          <FormControl
            key={key}
            orientation="horizontal"
            sx={{ justifyContent: "space-between" }}
          >
            <FormLabel>{label}</FormLabel>
            <Switch
              checked={Boolean(props[key as keyof typeof props])}
              onChange={(e) =>
                onUpdateProps({
                  [key]: e.target.checked,
                } as unknown as Partial<CanvasElement["props"]>)
              }
            />
          </FormControl>
        ))}
      </Stack>

      <FormControl>
        <FormLabel>Visible Columns</FormLabel>
        <Input
          size="sm"
          value={columnListValue}
          onChange={(e) =>
            onUpdateProps({
              visible_columns: e.target.value
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean),
            } as unknown as Partial<CanvasElement["props"]>)
          }
          placeholder="Leave blank for all visible view fields"
        />
        {fieldOptions.length > 0 && (
          <FormHelperText>
            Available: {fieldOptions.map((field) => field.key).join(", ")}
          </FormHelperText>
        )}
      </FormControl>
      <ViewGridFilterControlsEditor
        controls={props.filter_controls || []}
        fields={fieldOptions}
        onChange={(filter_controls) => onUpdateProps({ filter_controls })}
      />
    </>
  );
}

/**
 * AutoFillEditor - Configuration panel for auto-fill settings.
 */
interface AutoFillEditorProps {
  config: AutoFillConfig | undefined;
  onChange: (config: AutoFillConfig | undefined) => void;
  dataSources: CanvasDataSource[];
  viewFieldsCache: Record<string, ViewField[]>;
}

function AutoFillEditor({
  config,
  onChange,
  dataSources,
  viewFieldsCache,
}: AutoFillEditorProps) {
  const handleEnableChange = (enabled: boolean) => {
    if (enabled) {
      onChange({
        type: "current_user",
        userField: "email",
      });
    } else {
      onChange(undefined);
    }
  };

  const handleTypeChange = (type: AutoFillType) => {
    const baseConfig: AutoFillConfig = { type };

    switch (type) {
      case "current_user":
        baseConfig.userField = "email";
        break;
      case "expression":
        baseConfig.expression = "";
        break;
      case "lookup":
        baseConfig.lookupDataSource = "";
        baseConfig.lookupField = "";
        break;
    }

    onChange(baseConfig);
  };

  const handleUserFieldChange = (userField: AutoFillUserField) => {
    if (config) {
      onChange({ ...config, userField });
    }
  };

  const handleExpressionChange = (expression: string) => {
    if (config) {
      onChange({ ...config, expression });
    }
  };

  const handleLookupChange = (updates: Partial<AutoFillConfig>) => {
    if (config) {
      onChange({ ...config, ...updates });
    }
  };

  // Get fields for lookup data source
  const selectedLookupDs = config?.lookupDataSource
    ? dataSources.find(
        (ds) =>
          ds.id === config.lookupDataSource ||
          ds.name === config.lookupDataSource,
      )
    : undefined;
  const lookupViewId = selectedLookupDs?.view_id
    ? String(selectedLookupDs.view_id)
    : "";
  const lookupFields = lookupViewId ? viewFieldsCache[lookupViewId] || [] : [];

  return (
    <Stack spacing={2}>
      <FormControl
        orientation="horizontal"
        sx={{ justifyContent: "space-between" }}
      >
        <FormLabel>Enable Auto-Fill</FormLabel>
        <Switch
          checked={!!config}
          onChange={(e) => handleEnableChange(e.target.checked)}
        />
      </FormControl>

      {config && (
        <>
          <FormControl>
            <FormLabel>Auto-Fill Type</FormLabel>
            <Select
              value={config.type}
              onChange={(_, value) => handleTypeChange(value as AutoFillType)}
            >
              <Option value="current_user">Current User</Option>
              <Option value="current_date">Current Date</Option>
              <Option value="current_datetime">Current Date & Time</Option>
              <Option value="expression">Expression</Option>
              <Option value="lookup">Lookup</Option>
            </Select>
          </FormControl>

          {/* Current User options */}
          {config.type === "current_user" && (
            <FormControl>
              <FormLabel>User Field</FormLabel>
              <Select
                value={config.userField || "email"}
                onChange={(_, value) =>
                  handleUserFieldChange(value as AutoFillUserField)
                }
              >
                <Option value="id">User ID</Option>
                <Option value="email">Email</Option>
                <Option value="name">Display Name</Option>
                <Option value="entra_id">Entra ID</Option>
              </Select>
              <FormHelperText>
                Field from the current user to populate
              </FormHelperText>
            </FormControl>
          )}

          {/* Expression input */}
          {config.type === "expression" && (
            <FormControl>
              <FormLabel>Expression</FormLabel>
              <Input
                value={config.expression || ""}
                onChange={(e) => handleExpressionChange(e.target.value)}
                placeholder="e.g., {{today}} or {{user.name}}"
              />
              <FormHelperText>
                Template expression for dynamic values
              </FormHelperText>
            </FormControl>
          )}

          {/* Lookup configuration */}
          {config.type === "lookup" && (
            <>
              <FormControl>
                <FormLabel>Data Source</FormLabel>
                <Select
                  value={config.lookupDataSource || ""}
                  onChange={(_, value) =>
                    handleLookupChange({ lookupDataSource: value as string })
                  }
                  placeholder="Select data source"
                >
                  <Option value="">None</Option>
                  {dataSources.map((ds) => (
                    <Option key={ds.id} value={ds.id}>
                      {ds.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              {config.lookupDataSource && (
                <FormControl>
                  <FormLabel>Lookup Field</FormLabel>
                  {lookupFields.length > 0 ? (
                    <Select
                      value={config.lookupField || ""}
                      onChange={(_, value) =>
                        handleLookupChange({ lookupField: value as string })
                      }
                      placeholder="Select field"
                    >
                      <Option value="">None</Option>
                      {lookupFields.map((field) => (
                        <Option key={field.id} value={String(field.id)}>
                          {field.display_name ||
                            field.column?.display_name ||
                            field.alias ||
                            field.column?.column_name ||
                            "Unnamed Field"}
                        </Option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      value={config.lookupField || ""}
                      onChange={(e) =>
                        handleLookupChange({ lookupField: e.target.value })
                      }
                      placeholder="Enter field name"
                    />
                  )}
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Filter (Optional)</FormLabel>
                <Input
                  value={config.lookupFilter || ""}
                  onChange={(e) =>
                    handleLookupChange({ lookupFilter: e.target.value })
                  }
                  placeholder="e.g., status=active"
                />
                <FormHelperText>
                  Filter expression for lookup query
                </FormHelperText>
              </FormControl>
            </>
          )}

          {/* Preview */}
          <Box
            sx={{ p: 1.5, bgcolor: "background.level1", borderRadius: "sm" }}
          >
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              Preview:
            </Typography>
            <Typography level="body-sm" sx={{ fontFamily: "monospace" }}>
              {config.type === "current_user" &&
                `{{user.${config.userField || "email"}}}`}
              {config.type === "current_date" && "{{today}}"}
              {config.type === "current_datetime" && "{{now}}"}
              {config.type === "expression" && (config.expression || "(empty)")}
              {config.type === "lookup" &&
                `lookup(${config.lookupDataSource || "?"}.${config.lookupField || "?"})`}
            </Typography>
          </Box>
        </>
      )}
    </Stack>
  );
}

/**
 * ValidationRulesEditor - Configuration panel for validation rules.
 */
interface ValidationRulesEditorProps {
  rules: ValidationRule[];
  fieldType: string;
  onChange: (rules: ValidationRule[]) => void;
}

function ValidationRulesEditor({
  rules,
  fieldType,
  onChange,
}: ValidationRulesEditorProps) {
  const handleAddRule = (type: ValidationRuleType) => {
    const defaultMessages: Record<ValidationRuleType, string> = {
      required: "This field is required",
      min: "Value must be at least {value}",
      max: "Value must be at most {value}",
      minLength: "Must be at least {value} characters",
      maxLength: "Must be at most {value} characters",
      pattern: "Invalid format",
      custom: "Invalid value",
    };

    const newRule: ValidationRule = {
      type,
      message: defaultMessages[type],
    };

    onChange([...rules, newRule]);
  };

  const handleUpdateRule = (
    index: number,
    updates: Partial<ValidationRule>,
  ) => {
    const newRules = [...rules];
    newRules[index] = { ...newRules[index], ...updates };
    onChange(newRules);
  };

  const handleRemoveRule = (index: number) => {
    onChange(rules.filter((_, i) => i !== index));
  };

  // Determine which validation types are available based on field type
  const availableTypes: ValidationRuleType[] = ["required"];

  if (["number", "number-input"].includes(fieldType)) {
    availableTypes.push("min", "max");
  }

  if (["text", "text-input", "textarea", "json"].includes(fieldType)) {
    availableTypes.push("minLength", "maxLength", "pattern");
  }

  // Filter out already-used single-use types (like required)
  const existingTypes = new Set(rules.map((r) => r.type));
  const addableTypes = availableTypes.filter(
    (t) => !["required"].includes(t) || !existingTypes.has(t),
  );

  return (
    <Stack spacing={2}>
      {/* Existing rules */}
      {rules.map((rule, index) => (
        <Box
          key={index}
          sx={{
            p: 1.5,
            borderRadius: "sm",
            bgcolor: "background.level1",
            border: "1px solid",
            borderColor: "neutral.outlinedBorder",
          }}
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Chip size="sm" variant="soft" color="primary">
                {rule.type}
              </Chip>
              <IconButton
                size="sm"
                variant="plain"
                color="danger"
                onClick={() => handleRemoveRule(index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>

            {/* Value input for rules that need it */}
            {["min", "max", "minLength", "maxLength"].includes(rule.type) && (
              <FormControl size="sm">
                <FormLabel>Value</FormLabel>
                <Input
                  type="number"
                  value={rule.value !== undefined ? String(rule.value) : ""}
                  onChange={(e) =>
                    handleUpdateRule(index, {
                      value: e.target.value
                        ? Number(e.target.value)
                        : undefined,
                    })
                  }
                  placeholder="Enter value"
                />
              </FormControl>
            )}

            {/* Pattern input for regex */}
            {rule.type === "pattern" && (
              <FormControl size="sm">
                <FormLabel>Pattern (Regex)</FormLabel>
                <Input
                  value={(rule.value as string) || ""}
                  onChange={(e) =>
                    handleUpdateRule(index, { value: e.target.value })
                  }
                  placeholder="e.g., ^[a-zA-Z]+$"
                />
              </FormControl>
            )}

            {/* Custom validation expression */}
            {rule.type === "custom" && (
              <FormControl size="sm">
                <FormLabel>Validation Expression</FormLabel>
                <Input
                  value={(rule.value as string) || ""}
                  onChange={(e) =>
                    handleUpdateRule(index, { value: e.target.value })
                  }
                  placeholder="e.g., value > 0 && value < 100"
                />
              </FormControl>
            )}

            {/* Error message */}
            <FormControl size="sm">
              <FormLabel>Error Message</FormLabel>
              <Input
                value={rule.message}
                onChange={(e) =>
                  handleUpdateRule(index, { message: e.target.value })
                }
                placeholder="Error message"
              />
            </FormControl>
          </Stack>
        </Box>
      ))}

      {/* Add rule dropdown */}
      {addableTypes.length > 0 && (
        <FormControl size="sm">
          <FormLabel>Add Validation Rule</FormLabel>
          <Select
            value=""
            onChange={(_, value) => {
              if (value) {
                handleAddRule(value as ValidationRuleType);
              }
            }}
            placeholder="Select rule type..."
          >
            {addableTypes.map((type) => (
              <Option key={type} value={type}>
                {type === "required" && "Required"}
                {type === "min" && "Minimum Value"}
                {type === "max" && "Maximum Value"}
                {type === "minLength" && "Minimum Length"}
                {type === "maxLength" && "Maximum Length"}
                {type === "pattern" && "Pattern (Regex)"}
                {type === "custom" && "Custom Validation"}
              </Option>
            ))}
          </Select>
        </FormControl>
      )}

      {rules.length === 0 && (
        <Typography
          level="body-sm"
          sx={{ color: "text.tertiary", textAlign: "center" }}
        >
          No validation rules configured
        </Typography>
      )}
    </Stack>
  );
}

function WidgetCanvasConfig({
  element,
  dataSources,
  onUpdateProps,
}: {
  element: CanvasBuilderElement;
  dataSources: CanvasDataSource[];
  onUpdateProps: (props: Partial<CanvasElement["props"]>) => void;
}) {
  const props = element.props;
  const kind = String(props.widget_type || "CHART");
  const chart = (props.chart_config || {}) as Record<string, unknown>;
  const metric = (props.metric_config || {}) as Record<string, unknown>;
  const table = (props.table_config || {}) as Record<string, unknown>;
  const columns = (table.columns || []) as Array<Record<string, unknown>>;
  const sourceAlias = String(props.data_source_alias || "");
  const patch = (key: string, value: Record<string, unknown>) =>
    onUpdateProps({ [key]: value } as Partial<CanvasElement["props"]>);

  return (
    <Stack spacing={1.5}>
      <Divider />
      <Typography level="title-sm">
        {kind === "CHART"
          ? "Chart"
          : kind === "METRIC"
            ? "Metric"
            : "Summary Table"}
      </Typography>
      <FormControl>
        <FormLabel>Title</FormLabel>
        <Input
          value={String(props.title || "")}
          onChange={(event) => onUpdateProps({ title: event.target.value })}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Data source</FormLabel>
        <Select
          value={sourceAlias || null}
          onChange={(_, value) =>
            onUpdateProps({ data_source_alias: value || "" })
          }
        >
          {dataSources
            .filter((source) => source.view_id)
            .map((source) => (
              <Option key={source.id} value={source.id}>
                {source.name}
              </Option>
            ))}
        </Select>
      </FormControl>
      {kind === "CHART" && (
        <>
          <FormControl>
            <FormLabel>Chart type</FormLabel>
            <Select
              value={String(chart.chart_type || "bar")}
              onChange={(_, value) =>
                patch("chart_config", { ...chart, chart_type: value || "bar" })
              }
            >
              <Option value="bar">Bar</Option>
              <Option value="line">Line</Option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Category field</FormLabel>
            <Input
              value={String(chart.x_field || "")}
              onChange={(event) =>
                patch("chart_config", { ...chart, x_field: event.target.value })
              }
            />
          </FormControl>
          <FormControl>
            <FormLabel>Series fields (comma separated)</FormLabel>
            <Input
              value={((chart.y_fields || []) as string[]).join(", ")}
              onChange={(event) =>
                patch("chart_config", {
                  ...chart,
                  y_fields: event.target.value
                    .split(",")
                    .map((field) => field.trim())
                    .filter(Boolean),
                })
              }
            />
          </FormControl>
          <FormControl>
            <FormLabel>Aggregation</FormLabel>
            <Select
              value={String(chart.aggregation || "sum")}
              onChange={(_, value) =>
                patch("chart_config", { ...chart, aggregation: value || "sum" })
              }
            >
              {["count", "sum", "avg", "min", "max"].map((value) => (
                <Option key={value} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </FormControl>
        </>
      )}
      {kind === "METRIC" && (
        <>
          <FormControl>
            <FormLabel>Value field</FormLabel>
            <Input
              value={String(metric.value_field || "")}
              onChange={(event) =>
                patch("metric_config", {
                  ...metric,
                  value_field: event.target.value,
                })
              }
            />
          </FormControl>
          <FormControl>
            <FormLabel>Aggregation</FormLabel>
            <Select
              value={String(metric.aggregation || "count")}
              onChange={(_, value) =>
                patch("metric_config", {
                  ...metric,
                  aggregation: value || "count",
                })
              }
            >
              {["count", "sum", "avg", "min", "max"].map((value) => (
                <Option key={value} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Format</FormLabel>
            <Select
              value={String(metric.format || "number")}
              onChange={(_, value) =>
                patch("metric_config", { ...metric, format: value || "number" })
              }
            >
              <Option value="number">Number</Option>
              <Option value="currency">Currency (USD)</Option>
              <Option value="percentage">Percentage</Option>
            </Select>
          </FormControl>
        </>
      )}
      {kind === "TABLE" && (
        <>
          <FormControl>
            <FormLabel>Group field</FormLabel>
            <Input
              value={String(columns[0]?.field || "")}
              onChange={(event) =>
                patch("table_config", {
                  ...table,
                  columns: [
                    { ...(columns[0] || {}), field: event.target.value },
                    columns[1] || { field: "id", aggregation: "count" },
                  ],
                })
              }
            />
          </FormControl>
          <FormControl>
            <FormLabel>Measure field</FormLabel>
            <Input
              value={String(columns[1]?.field || "")}
              onChange={(event) =>
                patch("table_config", {
                  ...table,
                  columns: [
                    columns[0] || {},
                    { ...(columns[1] || {}), field: event.target.value },
                  ],
                })
              }
            />
          </FormControl>
          <FormControl>
            <FormLabel>Aggregation</FormLabel>
            <Select
              value={String(columns[1]?.aggregation || "count")}
              onChange={(_, value) =>
                patch("table_config", {
                  ...table,
                  columns: [
                    columns[0] || {},
                    { ...(columns[1] || {}), aggregation: value || "count" },
                  ],
                })
              }
            >
              {["count", "sum", "avg", "min", "max"].map((value) => (
                <Option key={value} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </FormControl>
        </>
      )}
      <FormHelperText>
        Aggregates use all matching view rows, up to 1000 result groups.
      </FormHelperText>
    </Stack>
  );
}
